import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import './global.css';

import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  DropdownButton,
  Form,
  Modal,
  Nav,
  Row,
  Navbar,
  Image,
  ListGroup,
  Badge,
  Tab,
  Tooltip,
  OverlayTrigger,
  InputGroup,
  Accordion,
  ButtonGroup,
  Table,
  FormControl
} from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import {
  ClipboardCheck,
  LogOut,
  PenTool,
  XCircle,
  Search,
  Bell
} from "lucide-react";
import { v4 as uuidv4 } from 'uuid'; // Import UUID for unique IDs
import { motion } from "framer-motion";
import { useSwipeable } from "react-swipeable";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// Firebase and other imports
import { initializeApp } from "firebase/app";
import firebaseConfig from "./firebaseConfig"; // adjust the path if needed
import { getFirestore, collection, doc, setDoc, deleteDoc, onSnapshot, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged, setPersistence, deleteUser } from 'firebase/auth';
import { getDoc } from "firebase/firestore";
import { browserLocalPersistence } from "firebase/auth";
import { initializeApp as initializeSecondaryApp } from "firebase/app";
import {
  getAuth as getSecondaryAuth,
  createUserWithEmailAndPassword as createUserWithEmailAndPasswordSecondary,
  signOut as signOutSecondary
} from "firebase/auth";


import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import { Menu } from "lucide-react";
import { Spinner } from 'react-bootstrap';
import { Collapse } from 'react-bootstrap'; // Import Collapse
import { debounce } from 'lodash';
import { sendPasswordResetEmail } from "firebase/auth";
import { GlassesBackgroundBar } from "./GlassesBackgroundBar";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,  // Alias Tooltip from chart.js
  Legend,
  ArcElement,
} from 'chart.js';
import { FaUser, FaUserMd, FaTasks, FaStopwatch, FaChartPie, FaCalendarCheck } from 'react-icons/fa';
import trimCanvas from 'trim-canvas';
import { Bar, Pie } from 'react-chartjs-2';
import { createPortal } from "react-dom";
import { FiSettings } from "react-icons/fi";
import Cropper from "react-easy-crop";
import { getCroppedImg } from './cropUtils'; // see below

import EyeFileUploader from "./EyeFileUploader";

/**
 * Load a Data-URL image, draw it onto a white canvas,
 * convert every pixel to pure black or white (luminosity threshold 128),
 * and return a new Data-URL.
 */
async function convertToBW(dataUrl) {
  // 1) load the image
  const img = document.createElement("img");
  img.src = dataUrl;
  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = rej;
  });

  // 2) draw to canvas with white background
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  // 3) threshold to pure black & white
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const luma = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    const bw = luma > 128 ? 255 : 0;
    d[i] = d[i + 1] = d[i + 2] = bw;
    d[i + 3] = 255;
  }
  ctx.putImageData(imageData, 0, 0);

  // 4) return the new Data-URL
  return canvas.toDataURL("image/png");
}


async function resizeImageFile(file, targetWidth = 600, targetHeight = 300) {
  // 1. load file into an image
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const img = document.createElement("img");
  img.src = dataUrl;
  await new Promise(r => (img.onload = r));

  // 2. draw into canvas at target size, preserving aspect via contain
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, targetWidth, targetHeight);

  // compute contain dimensions
  const ratio = Math.min(
    targetWidth / img.width,
    targetHeight / img.height
  );
  const w = img.width * ratio;
  const h = img.height * ratio;
  const x = (targetWidth - w) / 2;
  const y = (targetHeight - h) / 2;

  ctx.drawImage(img, x, y, w, h);

  // 3. return new smaller data URL
  return canvas.toDataURL("image/png");
}





// initialize the “secondary” app & auth
const secondaryApp = initializeSecondaryApp(firebaseConfig, "secondary");
const secondaryAuth = getSecondaryAuth(secondaryApp);

// near the top of your file, define your allowed specs:
const PREDEFINED_SPECS = ["cornea", "retina", "kids" /* etc */];

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ArcElement);




const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);
const auth = getAuth(app);


// ============================================================

// ------------------ Helper Functions ------------------
// Get the latest version of a task
function getCurrentVersion(task) {
  // Check if the versions property exists and is an array with at least one element
  if (task.versions && Array.isArray(task.versions) && task.versions.length > 0) {
    return task.versions[task.versions.length - 1];
  }
  // Return a default object if not
  return { status: "", files: [] };
}

const chartBackgroundPlugin = {
  id: 'custom_canvas_background_color',
  beforeDraw: (chart) => {
    const ctx = chart.canvas.getContext('2d');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#f5f5f5'; // your background color
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  }
};


function KPICard({ icon, label, value, bgColor }) {
  return (
    <div style={{
      flex: '1 1 200px',
      minWidth: '200px',
      background: bgColor,
      color: '#fff',
      padding: '20px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    }}>
      <div style={{ fontSize: '2rem' }}>{icon}</div>
      <div>
        <div style={{ fontSize: '1rem', marginBottom: '5px' }}>{label}</div>
        <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{value}</div>
      </div>
    </div>
  );

}

function getUserDisplayName(user) {
  // Use username, then username; if both missing, use the part of the email before '@'
  let first = user.username || user.username;
  if (!first && user.email) {
    first = user.email.split('@')[0];
  }
  first = first || "Unknown";
  const last = user.lastName || user.lastname || "";
  return `${first} ${last}`.trim();
}

function AdminAnalyticsDashboard({ tasks, users }) {
  const { t } = useTranslation();

  const analytics = useMemo(() => {
    const tasksPerDoctor = {};
    const timePerDoctor = {};
    const tasksPerCustomer = {};
    const statusCounts = {};

    tasks.forEach(task => {
      const current = getCurrentVersion(task);
      const doctorId = current.acceptedBy;
      const customerId = task.uploadedBy;
      const status = current.status;

      if (doctorId) tasksPerDoctor[doctorId] = (tasksPerDoctor[doctorId] || 0) + 1;
      if (customerId) tasksPerCustomer[customerId] = (tasksPerCustomer[customerId] || 0) + 1;
      if (status) statusCounts[status] = (statusCounts[status] || 0) + 1;

      if (doctorId && status === 'Finished') {
        const createdAt = new Date(task.versions[0].updatedAt);
        const finishedAt = new Date(current.updatedAt);
        const diffSeconds = (finishedAt - createdAt) / 1000;
        if (!timePerDoctor[doctorId]) {
          timePerDoctor[doctorId] = { totalTime: diffSeconds, count: 1 };
        } else {
          timePerDoctor[doctorId].totalTime += diffSeconds;
          timePerDoctor[doctorId].count++;
        }
      }
    });

    const avgTimePerDoctor = {};
    Object.keys(timePerDoctor).forEach(docId => {
      avgTimePerDoctor[docId] = (timePerDoctor[docId].totalTime / timePerDoctor[docId].count) / 3600;
    });

    return {
      tasksPerDoctor,
      tasksPerCustomer,
      avgTimePerDoctor,
      statusCounts
    };
  }, [tasks]);

  const doctorNames = {}, customerNames = {};
  users.forEach(u => {
    const name = u.username + ' ' + (u.lastName || '');
    if (u.role === 'doctor') doctorNames[u.id] = name;
    if (u.role === 'customer') customerNames[u.id] = name;
  });

  const totalDoctors = users.filter(u => u.role === 'doctor').length;
  const totalCustomers = users.filter(u => u.role === 'customer').length;
  const activeDoctors = new Set(Object.keys(analytics.tasksPerDoctor)).size;
  const activeCustomers = new Set(Object.keys(analytics.tasksPerCustomer)).size;

  const tasksThisMonth = tasks.filter(task => {
    const current = getCurrentVersion(task);
    const date = new Date(current.updatedAt);
    const now = new Date();
    return current.status === 'Finished' &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();
  }).length;

  const bestDoctor = Object.entries(analytics.avgTimePerDoctor).sort((a, b) => a[1] - b[1])[0]?.[0];
  const worstDoctor = Object.entries(analytics.avgTimePerDoctor).sort((a, b) => b[1] - a[1])[0]?.[0];

  // ——— New: doctor/customer tables ———
  const doctors = useMemo(() => users.filter(u => u.role === 'doctor'), [users])
  const customers = useMemo(() => users.filter(u => u.role === 'customer'), [users])

  const docCustomers = useMemo(() => {
    const map = {}
    doctors.forEach(d => {
      map[d.id] = customers.filter(
        c => c.mainDoctor === d.id || c.secondaryDoctor === d.id
      )
    })
    return map
  }, [doctors, customers])

  const unassigned = useMemo(
    () => customers.filter(c => !c.mainDoctor && !c.secondaryDoctor),
    [customers]
  )

  const [showList, setShowList] = useState(false)
  const [listTitle, setListTitle] = useState('')
  const [listItems, setListItems] = useState([])

  const openList = (title, items) => {
    setListTitle(title)
    setListItems(items)
    setShowList(true)
  }
  return (
    <div style={{ padding: '20px', background: '#f7f7f7', minHeight: '100vh', fontFamily: 'Arial' }}>
      <h2 style={{ textAlign: 'center' }}>{t('AdminAnalyticsDashboard')} </h2>

      <Row className="g-4 mb-4">
        <Col xs={12} sm={6} md={4}>
          <KPICard icon={<FaUser />} label={t('TotalCustomers')} value={totalCustomers} bgColor="#3498db" />
        </Col>
        <Col xs={12} sm={6} md={4}>
          <KPICard icon={<FaUserMd />} label={t('TotalDoctors')} value={totalDoctors} bgColor="#e67e22" />
        </Col>
        <Col xs={12} sm={6} md={4}>
          <KPICard icon={<FaUser />} label={t('ActiveCustomers')} value={activeCustomers} bgColor="#1abc9c" />
        </Col>
        <Col xs={12} sm={6} md={4}>
          <KPICard icon={<FaUserMd />} label={t('ActiveDoctors')} value={activeDoctors} bgColor="#8e44ad" />
        </Col>
        <Col xs={12} sm={6} md={4}>
          <KPICard icon={<FaTasks />} label={t('TasksThisMonth')} value={tasksThisMonth} bgColor="#16a085" />
        </Col>
      </Row>



      {/* Redesigned Doctor Activity */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="custom-card-header">
              {t('DoctorActivity')}
            </Card.Header>
            <Card.Body className="py-2 px-3">
              <Table responsive hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>{t('Name')}</th>
                    <th>{t('Status')}</th>
                    <th>{t('LastActive')}</th>
                  </tr>
                </thead>
                <tbody>
                  {doctors.map(d => (
                    <tr key={d.id}>
                      <td>{getUserDisplayName(d)}</td>
                      <td>{d.isAvailable ? '🟢 Active' : '🔴 Inactive'}</td>
                      <td>
                        {d.lastLogin
                          ? (
                            d.lastLogin.toDate
                              ? d.lastLogin.toDate().toLocaleString()  // Firestore Timestamp
                              : new Date(d.lastLogin).toLocaleString() // JS Date/string
                          )
                          : '—'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>


      {/* 2) Customers by Doctor */}
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="custom-card-header">
              {t('CustomersbyDoctor')}
            </Card.Header>
            <Card.Body className="py-2 px-3">
              <Table responsive hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>{t('Doctor')}</th>
                    <th>{t('Patients')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {doctors.map(d => (
                    <tr key={d.id}>
                      <td>{getUserDisplayName(d)}</td>
                      <td>{docCustomers[d.id].length}</td>
                      <td className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openList(getUserDisplayName(d), docCustomers[d.id])}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td>{t('Unassigned')}</td>
                    <td>{unassigned.length}</td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openList('Unassigned', unassigned)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Redesigned Modal */}
      <Modal
        show={showList}
        onHide={() => setShowList(false)}
        size="lg"
        centered
        dialogClassName="modal-dialog-centered"
        contentClassName="border-0 bg-transparent"
      >
        <Modal.Header closeButton className="custom-card-header" />

        <Modal.Body className="p-0">
          <Card className="shadow-sm">
            <Card.Header className="custom-card-header">
              {listTitle}
            </Card.Header>
            <Card.Body className="p-3">
              <Table responsive hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {listItems.map(c => (
                    <tr key={c.id}>
                      <td>{getUserDisplayName(c)}</td>
                      <td>{c.email}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Modal.Body>
      </Modal>



      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <div style={{
          display: 'inline-flex',
          gap: '1.5rem',
          alignItems: 'center',
          whiteSpace: 'nowrap'
        }}>
          <strong>{t('FastestDoctor')}</strong> {doctorNames[bestDoctor] || 'N/A'}
          <strong>{t('SlowestDoctor')}</strong> {doctorNames[worstDoctor] || 'N/A'}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '30px', marginBottom: '40px' }}>
        <Card className="p-4" style={{ width: '320px', height: '260px', backgroundColor: '#f5f5f5' }}>
          <h6 style={{ textAlign: 'center' }}>{t('TaskStatusBreakdown')}</h6>
          <Pie
            data={{
              labels: Object.keys(analytics.statusCounts),
              datasets: [{
                data: Object.values(analytics.statusCounts),
                backgroundColor: ['#3498db', '#e74c3c', '#2ecc71', '#f1c40f']
              }]
            }}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: { position: 'bottom' },
                datalabels: {
                  color: '#fff',
                  formatter: value => value,
                  font: { weight: 'bold' }
                },
                custom_canvas_background_color: true
              }
            }}
            plugins={[chartBackgroundPlugin]}
          />
        </Card>

        <Card className="p-4" style={{ width: '320px', height: '260px', backgroundColor: '#f5f5f5' }}>
          <h6 style={{ textAlign: 'center' }}>{t('TasksperDoctor')}</h6>
          <Bar
            data={{
              labels: Object.keys(analytics.tasksPerDoctor).map(id => doctorNames[id] || id),
              datasets: [{
                label: 'Tasks',
                data: Object.values(analytics.tasksPerDoctor),
                backgroundColor: '#3498db'
              }]
            }}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: { display: false },
                datalabels: {
                  anchor: 'end',
                  align: 'top',
                  formatter: Math.round,
                  font: { weight: 'bold' }
                },
                custom_canvas_background_color: true
              }
            }}
            plugins={[chartBackgroundPlugin]}
          />
        </Card>
      </div>
    </div>
  );
}


const uploadFileToStorage = async (file, taskId) => {
  // Create a reference in the "tasks" folder using the taskId and file name
  const fileRef = ref(storage, `tasks/${taskId}/${file.name}`);

  // Upload the file
  await uploadBytes(fileRef, file);

  // Get and return the file's download URL
  const downloadURL = await getDownloadURL(fileRef);
  return downloadURL;
};

// ------------------ DoctorNotesEditor Component ------------------
function DoctorNotesEditor({ notes, onChangeNotes, onSaveNotes, isLocked }) {
  return (
    <div>
      <Form.Control
        as="textarea"
        placeholder="Add Notes"
        value={notes}
        onChange={(e) => onChangeNotes(e.target.value)}
        className="mb-2"
        disabled={isLocked}
        rows={3}
        aria-label="Doctor Notes"
      />
    </div>
  );
}

// --- FileMetadataModal (inline) ---
function FileMetadataModal({ show, files, onCancel, onConfirm, ...modalProps }) {
  const [metadata, setMetadata] = React.useState([]);
  const { t } = useTranslation();

  // **REINITIALIZE** metadata whenever `files` changes:
  React.useEffect(() => {
    setMetadata(
      files.map(f => ({
        id: f.id,
        file: f.file,
        eye: '',
        customTitle: ''
      }))
    );
  }, [files]);

  const eyeOptions = [
    'Disc Map both eyes',
    'Macula Map R',
    'Macula Map L',
    'Macula Multi R',
    'Macula Multi L',
    'Fundus R',
    'Fundus L'
  ];
  const handleEyeChange = (id, value) => {
    setMetadata(m => m.map(md => md.id === id ? { ...md, eye: value } : md));
  };
  const handleTitleChange = (id, value) => {
    setMetadata(m => m.map(md => md.id === id ? { ...md, customTitle: value } : md));
  };
  return (
    <Modal show={show}
      onHide={onCancel}
      size="lg" centered
      backdrop="static"
      keyboard={false}
      {...modalProps} >
      <Modal.Header className="custom-card-header" closeButton>
        <Modal.Title>{t('LabelYourFiles')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ListGroup>
          {metadata.map(md => (
            <ListGroup.Item key={md.id} className="d-flex gap-3">
              <Image src={URL.createObjectURL(md.file)} thumbnail width={100} />
              <div style={{ flex: 1 }}>
                <Form.Group className="mb-2">
                  <Form.Label>{t('LabelYourFiles')}</Form.Label>
                  <Form.Select value={md.eye} onChange={e => handleEyeChange(md.id, e.target.value)}>
                    <option value="">{t('Select')}</option>
                    {eyeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{t('CustomTitleNote')}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter custom title or note"
                    value={md.customTitle}
                    onChange={e => handleTitleChange(md.id, e.target.value)}
                  />
                </Form.Group>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>{t('Cancel')}</Button>
        <Button variant="primary" onClick={() => onConfirm(metadata)}>{t('save')}</Button>
      </Modal.Footer>
    </Modal>
  );
}
// --- end FileMetadataModal ---


// ------------------ Main App Component ------------------
function App() {

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), snap => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);



  const [isLoading, setIsLoading] = useState(false);

  const { t, i18n } = useTranslation();



  // ------------------ State Variables ------------------
  const [uploading, setUploading] = useState(false);

  // Filtering tasks by status (e.g., "All", "Not Accepted", "Returned", etc.)
  const [selectedStatus, setSelectedStatus] = useState('All');

  // Search State (Enhancement 5)
  const [searchTerm, setSearchTerm] = useState('');

  // Sorting State (Enhancement 7)
  const [sortOrder, setSortOrder] = useState('Newest First');

  // your processing-lock for pending writes
  const [isProcessing, setIsProcessing] = useState(false);


  // New: initialize users as an empty array.
  const [users, setUsers] = useState([]);


  // Initialize tasks with valid uploadedBy
  const [tasks, setTasks] = useState([]);
  const [patientID, setPatientID] = useState("");
  const [age, setAge] = useState("");
  const [diseases, setDiseases] = useState("");
  const [taskNotes, setTaskNotes] = useState("");  // renamed from "notes" to avoid confusion with doctor notes



  const [uploadProgress, setUploadProgress] = useState(0);


  const [showSideMenu, setShowSideMenu] = useState(false);
  const [licenseFile, setLicenseFile] = useState(null);

  const [storeInfo, setStoreInfo] = useState(null);

  // --- Put this at the top of App(), with your other hooks ---
  const [showReturnedModal, setShowReturnedModal] = useState(false);
  const [returnedModalTasks, setReturnedModalTasks] = useState([]);


  const [showMetaModal, setShowMetaModal] = useState(false);
  const [isReuploading, setIsReuploading] = useState(false);

  const [stagedFiles, setStagedFiles] = useState([]);      // raw File objects
  const [fileMeta, setFileMeta] = useState([]);      // metadata array

  const [isMagnified, setIsMagnified] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0, imgW: 0, imgH: 0, pageX: 0, pageY: 0 });



  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);


  // cropping state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, croppedArea) => {
    setCroppedAreaPixels(croppedArea);
  }, []);



  /* const [showCustomerSignatureModal, setShowCustomerSignatureModal] = useState(false);*/
  /* const customerSignatureCanvasRef = useRef(null);*/


  // ================== FIRESTORE REALTIME SUBSCRIPTION ==================
  // This will load tasks from Firestore and overwrite `tasks` in real-time.
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "tasks"), (snapshot) => {
      const tasksFromDB = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const visibleTasks = tasksFromDB.filter(task => {
        const current = getCurrentVersion(task);
        return current.status !== "Returned";
      });

      setTasks(visibleTasks);
    });

    return () => unsubscribe();
  }, []);


  useEffect(() => {
    const unsubscribeUsers = onSnapshot(collection(db, "users"), (snapshot) => {
      const usersFromDB = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setUsers(usersFromDB); // state gets updated after this call
    });
    return () => unsubscribeUsers();
  }, []);

  // This effect will log the updated users state whenever it changes:
  useEffect(() => {

  }, [users]);

  // ➕ auto-offline any doctor idle >48h, but never auto-online
  useEffect(() => {
    if (!users.length) return;
    const cutoff = Date.now() - 72 * 60 * 60 * 1000; // 48h ago

    users.forEach(async u => {
      if (u.role !== "doctor") return;

      // compute lastLogin timestamp
      const last = u.lastLogin
        ? (u.lastLogin.toDate
          ? u.lastLogin.toDate().getTime()
          : new Date(u.lastLogin).getTime()
        )
        : 0;

      // if they’ve been idle *over* cutoff AND they’re still marked available, set offline
      if (last < cutoff && u.isAvailable) {
        const ref = doc(db, "users", u.id);
        await updateDoc(ref, { isAvailable: false });
      }
    });
  }, [users]);




  // Set the direction of the page based on the selected language
  useEffect(() => {
    document.documentElement.dir = i18n.language === 'he' ? 'rtl' : 'ltr';
  }, [i18n.language]);



  const toggleSideMenu = () => {
    setShowSideMenu(prev => !prev);
  };

  // =====================================================================


  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      // Now perform the sign-in operation here.
    })
    .catch((error) => {
      console.error("Error setting persistence:", error);
    });



  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCurrentUser(null)
        setLoggedIn(false)
        return
      }
      const ref = doc(db, "users", user.uid)
      const snap = await getDoc(ref)
      if (!snap.exists()) return
      const data = snap.data()
      const nowISO = new Date().toISOString()

      // always update lastLogin
      const updates = { lastLogin: nowISO }

      // only set online if the field is truly missing (undefined)
      if (data.role === "doctor" && data.isAvailable === undefined) {
        updates.isAvailable = true
      }

      await updateDoc(ref, updates)

      // re-fetch to get the merged result
      const fresh = (await getDoc(ref)).data()
      setCurrentUser(fresh)
      setUserRole(fresh.role)
      setLoggedIn(true)
    })
    return () => unsub()
  }, [])





  /************************/
  useEffect(() => {
    const loginTimestamp = localStorage.getItem('loginTimestamp');
    if (loginTimestamp) {
      const timeDiff = Date.now() - parseInt(loginTimestamp, 10);
      // 1 hour = 3600000 milliseconds
      if (timeDiff > 3600000) {
        // More than 1 hour has passed—end the session.
        auth.signOut();
        localStorage.removeItem('loginTimestamp');
        setFeedback({ type: 'info', message: "Your session has expired. Please sign in again." });
        setLoggedIn(false);
        setCurrentUser(null);
      }
    }
  }, []);


  /****************************************/
  useEffect(() => {
    const refreshSession = () => {
      localStorage.setItem('loginTimestamp', Date.now().toString());
    };

    // Listeners for various user actions
    window.addEventListener('mousemove', refreshSession);
    window.addEventListener('keydown', refreshSession);

    return () => {
      window.removeEventListener('mousemove', refreshSession);
      window.removeEventListener('keydown', refreshSession);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isProcessing) {
        // Standard way to trigger the “Are you sure?” dialog
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isProcessing]);


  // Return Task Modal
  const [showReturnTaskModal, setShowReturnTaskModal] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [returningTaskId, setReturningTaskId] = useState(null);

  // Login / Logout
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggedIn, setLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  // Doctor signature
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const signatureCanvasRef = useRef(null);
  const doctorSigRef = useRef(null);
  const [userToSignId, setUserToSignId] = useState(null);

  // Admin Portal
  const [userData, setUserData] = useState({
    username: "",
    lastName: "",
    email: "",
    idNumber: "",
    licenseNumber: "",
    address: "",
    role: "customer",
    password: "",
    storeName: "", // Enhancement 8
    doctorType: "Doctor", // Enhancement 9
    specialization: "",
    otherSpecialization: "",  // <— add this
    anydeskuser: "",
    PhoneNumber: "",
    anydeskpassword: "",
    mainDoctor: "",
    secondaryDoctor: "",
  });

  // Edit existing user states
  const [editingUserId, setEditingUserId] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserData, setEditUserData] = useState({
    username: "",
    lastName: "",
    email: "",
    idNumber: "",
    licenseNumber: "",
    address: "",
    role: "customer",
    password: "",
    doctorType: "Doctor", // Enhancement 9
    specialization: "",
    otherSpecialization: "",  // <— add this
    storeName: "", // Enhancement 8
    signature: "",
    licenseFileUrl: "", // optional, only used for preview
    anydeskuser: "",
    PhoneNumber: "",
    anydeskpassword: "",
    mainDoctor: "",
    secondaryDoctor: "",
  });


  useEffect(() => {
    if (!showEditUserModal || !editingUserId) return;

    const unsub = onSnapshot(
      doc(db, "users", editingUserId),
      (snapshot) => {
        if (snapshot.exists()) {
          setEditUserData({
            id: snapshot.id,
            ...snapshot.data(),  // pulls in licenseFileUrl, etc.
          });
        }
      }
    );
    return () => unsub();
  }, [showEditUserModal, editingUserId]);

  // History Modal
  const [historyTask, setHistoryTask] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Feedback Messages
  const [feedback, setFeedback] = useState({ type: '', message: '' });


  // Delete Confirmation Modal State
  const [showDeleteTaskModal, setShowDeleteTaskModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);

  // Use a lowercase variable name for the user to delete
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteUser, setShowDeleteUser] = useState(false);

  // Re-upload Modal State
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [reuploadTaskId, setReuploadTaskId] = useState(null);
  const [reuploadFile, setReuploadFile] = useState(null);

  // Signed Notes Modal State
  const [showSignedNotesModal, setShowSignedNotesModal] = useState(false);
  const [signedNotesFile, setSignedNotesFile] = useState(null);

  // Task Detail Modal State (for viewing details)
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);

  // File Input Reference (Defined only once)
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]); // Initialize selectedFiles as an empty array

  // Custom Task Name State (Enhancement 6)
  const [customTaskName, setCustomTaskName] = useState("");

  const [showReportModalDoc, setShowReportModalDoc] = useState(false);
  const [reportStartDateDoc, setReportStartDateDoc] = useState("");
  const [reportEndDateDoc, setReportEndDateDoc] = useState("");
  const [selectedCustomerDoc, setSelectedCustomerDoc] = useState("");
  // For the doctor report modal (if needed)
  const [reportStatus, setReportStatus] = useState(""); // if you want to filter by task status


  // For the customer report modal
  const [showReportModalcustomer, setShowReportModalcustomer] = useState(false);
  const [reportStartDatecustomer, setReportStartDatecustomer] = useState("");
  const [reportEndDatecustomer, setReportEndDatecustomer] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("");

  const [showTaskHistory, setShowTaskHistory] = useState(false);

  const [showFilePreview, setShowFilePreview] = useState(false);
  const [fileList, setFileList] = useState([]); // Array of file objects: { fileUrl, fileType, fileName }
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const [showFinishedTasks, setShowFinishedTasks] = useState(false);

  const [showExpandedEditor, setShowExpandedEditor] = useState(false);
  const [currentEditingTaskId, setCurrentEditingTaskId] = useState(null);

  // for the bell-icon modal
  const [showNotifications, setShowNotifications] = useState(false);

  // open/close helpers
  const openNotificationsModal = () => setShowNotifications(true);
  const closeNotificationsModal = () => setShowNotifications(false);
  const [pendingTasks, setPendingTasks] = useState({}); // { [taskId]: { fade: true, cancelTimer: timeout, confirmTimer: timeout } }


  const [showMeta, setShowMeta] = useState(false);
  const [files, setFiles] = useState([]);

  const [allowDoctorEdit, setAllowDoctorEdit] = useState(true);
  const [allowCustomerEdit, setAllowCustomerEdit] = useState(true);



  const [showDoctorEditModal, setShowDoctorEditModal] = useState(false);
  const [doctorDetails, setDoctorDetails] = useState({
    PhoneNumber: '',
    idNumber: '',
    licenseNumber: '',
    specialization: '',
    otherSpecialization: '',
    licenseFileUrl: ''
  });

  // ★ Customer edit modal state
  const [showCustomerEditModal, setShowCustomerEditModal] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    storeName: '',
    lastName: '',
    PhoneNumber: '',
    address: '',
    anydeskuser: '',
    anydeskpassword: ''
  });


  // ① Modal visibility
  const [showReqFieldsModal, setShowReqFieldsModal] = useState(false);

  // App.js (near the top)
  // Required fields defaults
  const requiredFieldsDefaults = {
    email: true,
    PhoneNumber: false,
    address: false,
    storeName: true,
    lastName: false,
    username: false,
    password: false,
    role: false,
    idNumber: false,
    licenseNumber: false,
    doctorType: false,
    specialization: false,
    otherSpecialization: false,
    mainDoctor: false,
    secondaryDoctor: false,
    anydeskuser: false,
    anydeskpassword: false,
    licenseFile: false
  };

  // Grouping keys by role
  const doctorOnlyKeys = [
    "licenseFile",
    "doctorType",
    "specialization",
    "otherSpecialization",
    "licenseNumber"
  ];
  const customerOnlyKeys = [
    "storeName",
    "mainDoctor",
    "secondaryDoctor",
    "anydeskuser",
    "anydeskpassword"
  ];




  // ② The live config object
  // App.js, inside function App() { … }
  // State and snapshot
  const [requiredFieldsConfig, setRequiredFieldsConfig] = useState(requiredFieldsDefaults);


  const SMS_ENDPOINT =
    process.env.REACT_APP_SMS_ENDPOINT ||
    'https://optimizesign-sms.onrender.com/send-notification';

  const openStoreInfo = (task) => {
    // Find the customer information from the users array using task.uploadedBy
    const customer = users.find(u => u.id === task.uploadedBy);
    if (customer) {
      setStoreInfo(customer);
    } else {
      setFeedback({ type: 'warning', message: 'Customer information not found.' });
    }
  };

  const alreadySigned = useRef({});

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "pendingSigns"), snapshot => {
      snapshot.docChanges().forEach(change => {
        const tid = change.doc.id;
        const data = change.doc.data();
        const { startAt, duration } = data;

        if (!startAt || !duration) return;

        // Clear previous timers
        const existing = pendingTasks[tid];
        if (existing) {
          clearInterval(existing.intervalId);
          clearTimeout(existing.cancelTimer);
          clearTimeout(existing.confirmTimer);
        }

        // Handle removal
        if (change.type === "removed") {
          setPendingTasks(prev => {
            const copy = { ...prev };
            delete copy[tid];
            return copy;
          });
          return;
        }

        // Handle new or modified task
        const now = Date.now() / 1000;
        const started = startAt.toMillis() / 1000;
        const elapsed = Math.floor(now - started);
        const remaining = duration - elapsed;

        if (remaining <= 0) {
          if (!alreadySigned.current[tid]) {
            alreadySigned.current[tid] = true;
            confirmSign(tid);
          }
        } else {
          rebuildTimers(tid, remaining, duration);
        }
      });
    });

    return () => unsub();
  }, []);

  // alongside your other settings‐state
  const [autoSignDelay, setAutoSignDelay] = useState(1);

  // in your useEffect that listens on "settings/userOptions":
  useEffect(() => {
    const ref = doc(db, "settings", "userOptions");
    return onSnapshot(ref, snap => {
      if (snap.exists()) {
        const data = snap.data();
        setAllowDoctorEdit(data.allowDoctorEdit ?? true);
        setAllowCustomerEdit(data.allowCustomerEdit ?? true);
        setAutoSignDelay(data.autoSignDelayMinutes ?? 15);
      }
    });
  }, []);


  useEffect(() => {
    const ref = doc(db, "settings", "userOptions");
    return onSnapshot(ref, snap => {
      if (snap.exists()) {
        const data = snap.data();
        setAllowDoctorEdit(data.allowDoctorEdit ?? true);
        setAllowCustomerEdit(data.allowCustomerEdit ?? true);
        setAutoSignDelay(data.autoSignDelayMinutes ?? 15);
      }
    });
  }, []);


  // in your AuthProvider (or top‐level App)
  useEffect(() => {
    if (!currentUser?.id) return;                    // ← use .id, not .uid
    const userRef = doc(db, "users", currentUser.id);
    const unsub = onSnapshot(userRef, snap => {
      if (snap.exists()) {
        setCurrentUser({ id: snap.id, ...snap.data() });
      }
    });
    return () => unsub();
  }, [currentUser?.id]);                              // ← dependency on .id


  const [enableReminderSMS, setEnableReminderSMS] = useState(false);
  const [reminderDelayHours, setReminderDelayHours] = useState(1); // default 1 hour


  // at top of App()
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!auth.currentUser) return;
    auth.currentUser
      .getIdTokenResult()
      .then(idTokenResult => setIsAdmin(!!idTokenResult.claims.admin));
  }, [auth.currentUser]);

  useEffect(() => {
    const ref = doc(db, "settings", "userOptions");
    return onSnapshot(ref, snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      setAllowDoctorEdit(d.allowDoctorEdit ?? true);
      setAllowCustomerEdit(d.allowCustomerEdit ?? true);
      setAutoSignDelay(d.autoSignDelayMinutes ?? 15);
      setEnableReminderSMS(d.enableReminderSMS ?? false);           // ←
      setReminderDelayHours(d.reminderDelayHours ?? 1);   // ← new field
    });
  }, []);



  const handleSignatureFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    setSignatureFile(file);
    const reader = new FileReader();
    reader.onload = () => setSignaturePreview(reader.result);
    reader.readAsDataURL(file);
  };

  // helper to rebuild all three timers & React state
  function rebuildTimers(taskId, remaining, totalDuration) {
    // copy your intervalId / cancelTimer / confirmTimer logic
    const intervalId = setInterval(() => {
      setPendingTasks(prev => {
        const cur = prev[taskId];
        if (!cur || cur.remaining <= 1) return prev;
        return {
          ...prev,
          [taskId]: { ...cur, remaining: cur.remaining - 1 }
        };
      });
    }, 1000);

    const cancelTimer = setTimeout(() => {
      setPendingTasks(prev => ({
        ...prev,
        [taskId]: { ...prev[taskId], cancelTimer: null }
      }));
    }, (totalDuration - remaining) * 1000);

    const confirmTimer = setTimeout(() => {
      confirmSign(taskId);
    }, remaining * 1000);

    setPendingTasks(prev => ({
      ...prev,
      [taskId]: { remaining, intervalId, cancelTimer, confirmTimer }
    }));
  }


  const requiredFields = [
    'username',    // First Name
    'lastName',    // Last Name
    'email',
    'password',
    'PhoneNumber'  // Phone
  ];

  // helper to do the final sign & cleanup
  async function confirmSign(taskId) {
    try {
      // perform your existing handleSignTask + sendNotificationEmail logic…
      // then clear the React state:
      if (pendingTasks[taskId]?.intervalId) {
        clearInterval(pendingTasks[taskId].intervalId);
      }

      setPendingTasks(prev => {
        const copy = { ...prev };
        delete copy[taskId];
        return copy;
      });
      // and remove the Firestore doc so it won’t re-fire
      await deleteDoc(doc(db, "pendingSigns", taskId));
    } catch (err) {
      console.error("Error in confirmSign:", err);
      setFeedback({ type: "error", message: "Signing failed." });
    }

  }
  const handleDelayedSignClick = async (taskId) => {
    const existing = pendingTasks[taskId];

    // ——— Cancel any in-flight timers ———
    if (existing) {
      // clear all JS timers
      clearInterval(existing.intervalId);
      clearTimeout(existing.cancelTimer);
      clearTimeout(existing.confirmTimer);

      // remove the persisted Firestore marker
      await deleteDoc(doc(db, "pendingSigns", taskId));

      // remove from local React state
      setPendingTasks(prev => {
        const copy = { ...prev };
        delete copy[taskId];
        return copy;
      });
      return;
    }

    // ——— No existing pending: start a fresh 15-minute countdown ———
    // derive seconds from the admin‐set minutes
    const totalDuration = (autoSignDelay || 1) * 60;


    // write the start + duration to Firestore
    await setDoc(doc(db, "pendingSigns", taskId), {
      startAt: serverTimestamp(),
      duration: totalDuration
    });

    // local ticking state
    let remaining = totalDuration;
    const intervalId = setInterval(() => {
      setPendingTasks(prev => {
        const cur = prev[taskId];
        if (!cur) return prev;
        const next = cur.remaining - 1;
        if (next <= 0) clearInterval(intervalId);
        return {
          ...prev,
          [taskId]: { ...cur, remaining: next }
        };
      });
    }, 1000);

    // when cancel-window ends (after full duration), flip cancelTimer→null
    const cancelTimer = setTimeout(() => {
      setPendingTasks(prev => ({
        ...prev,
        [taskId]: { ...prev[taskId], cancelTimer: null }
      }));
    }, totalDuration * 1000);

    // when countdown finishes, do the signing and cleanup
    const confirmTimer = setTimeout(async () => {
      try {
        const { signedHtmlUrl: url, fileName } = await handleSignTask(taskId);

        // clear all timers
        clearInterval(intervalId);
        clearTimeout(cancelTimer);
        clearTimeout(confirmTimer);

        // remove Firestore marker
        await deleteDoc(doc(db, "pendingSigns", taskId));

        // remove from UI state
        setPendingTasks(prev => {
          const copy = { ...prev };
          delete copy[taskId];
          return copy;
        });

        // …your existing email/feedback code…
      } catch (err) {
        console.error(err);
        setFeedback({ type: 'error', message: 'Error during auto-sign.' });
      }
    }, totalDuration * 1000);

    // store all timers in React state
    setPendingTasks(prev => ({
      ...prev,
      [taskId]: { remaining, intervalId, cancelTimer, confirmTimer }
    }));
  };




  const handleMetaCancel = () => {
    setFiles([]);          // ← wipe the attachments
    setShowMeta(false);    // ← hide the modal
  };


  /**
   * Send an SMS via your Render-hosted Inforu proxy.
   * @param {Object} user           – user object with .PhoneNumber or .phoneNumber and .username
   * @param {string} customMessage  – override message text
   * @returns {Promise<boolean>}    – true if sent, false on any failure
   */
  async function sendNotificationMessage(user, customMessage) {
    try {
      // 1) Grab and normalize the phone
      const raw = user.PhoneNumber || user.phoneNumber;
      if (!raw) {
        console.warn('🚫 No phone number provided for user', user);
        return false;
      }
      const phone = raw.startsWith('0')
        ? '972' + raw.slice(1)
        : raw;

      // 2) Build the payload
      const payload = {
        to: phone,
        message:
          customMessage ||
          `📢 שלום ${user.username}, הפניה שלך עבור - - פוענחה OCT-Avnet`
      };

      // 3) Fire off the POST
      const response = await fetch(SMS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // 4) Try to parse JSON—Inforu returns JSON even on error
      const result = await response.json().catch(() => null);

      if (!response.ok) {
        console.error('❌ Failed to send notification');
        return false;
      }

      console.log('✅ SMS sent successfully:');
      return true;

    } catch (error) {
      console.error('💥 Error sending notification:');
      return false;
    }
  }


  const handleResendTask = async (taskId) => {
    // Find the task to update
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // Check if the task status is "Returned"
    if (getCurrentVersion(task).status !== "Returned") {
      setFeedback({ type: 'danger', message: "Cannot resend task because it is not returned." });
      return;
    }

    // Otherwise, update the task's version to reset its status
    const updatedTask = pushNewVersion(task, {
      status: "Not Accepted", // Or any other status that makes sense
      returnReason: "",       // Clear the return reas
      updatedAt: new Date().toISOString(),
    });

    try {
      await setDoc(doc(db, "tasks", updatedTask.id), updatedTask);
      setTasks(prevTasks =>
        prevTasks.map(t => t.id === taskId ? updatedTask : t)
      );
      setFeedback({ type: 'success', message: "Task resent successfully." });
      const customer = users.find(u => u.id === task.uploadedBy);
      if (!customer) return;

      const mainDoctor = users.find(u => u.id === customer.mainDoctor);
      const secondaryDoctor = users.find(u => u.id === customer.secondaryDoctor);

      let targetDoctor = null;
      if (mainDoctor?.isAvailable) {
        targetDoctor = mainDoctor;
      } else if (secondaryDoctor) {
        targetDoctor = secondaryDoctor;
      }

      if (targetDoctor) {
        const notifId = crypto.randomUUID();
        await setDoc(doc(db, 'notifications', notifId), {
          toUserId: targetDoctor.id,
          message: "📢 משימה חדשה הועלתה עבורך למערכת לבדיקה.",
          createdAt: Timestamp.now(),
        });

        await sendNotificationMessage(targetDoctor, `📢 משימה חדשה הועלתה עבורך למערכת לבדיקה.`);
      }
    }
    catch (error) {
      console.error("Error resending task:", error);
      setFeedback({ type: 'danger', message: "Failed to resend task." });
    }
  };



  // Function to open the file browser modal with files from a task:
  const handleOpenFileBrowser = (item) => {
    // Assume item is either a task or a version with a "files" array.
    let files = [];
    if (item.files && item.files.length > 0) {
      files = item.files;
    }
    // Format the files if necessary:
    const formattedFiles = files.map(file => ({
      fileUrl: file.fileUrl,
      fileType: file.fileType,
      fileName: file.fileName,
      eye: file.eye || '',
      customTitle: file.customTitle || ''
    }));


    setFileList(formattedFiles);
    setCurrentFileIndex(0);
    setShowFilePreview(true);
  };


  async function handleLicenseUpload(file) {
    // 1. upload to Storage...
    const storageRef = ref(storage, `licenses/${editUserData.id}/${file.name}`);
    await uploadBytes(storageRef, file);

    // 2. get download URL
    const url = await getDownloadURL(storageRef);

    // 3. write the URL into Firestore
    await updateDoc(doc(db, 'users', editUserData.id), {
      licenseFileUrl: url
    });

    // 4. update your local state so the button shows immediately
    setEditUserData(prev => ({
      ...prev,
      licenseFileUrl: url
    }));
  }

  async function handleSaveCustomerDetails() {
    const uid = currentUser.uid || currentUser.id;
    if (!uid) {
      setFeedback({ type: 'danger', message: t('SaveFailed') });
      return;
    }

    try {
      await setDoc(
        doc(db, "users", uid),
        {
          storeName: customerDetails.storeName,
          lastName: customerDetails.lastName,
          PhoneNumber: customerDetails.PhoneNumber,
          address: customerDetails.address,
          anydeskuser: customerDetails.anydeskuser,
          anydeskpassword: customerDetails.anydeskpassword
        },
        { merge: true }
      );

      // **NEW**: update your in-memory user
      setCurrentUser(prev => ({
        ...prev,
        storeName: customerDetails.storeName,
        lastName: customerDetails.lastName,
        PhoneNumber: customerDetails.PhoneNumber,
        address: customerDetails.address,
        anydeskuser: customerDetails.anydeskuser,
        anydeskpassword: customerDetails.anydeskpassword
      }));

      setShowCustomerEditModal(false);
      setFeedback({ type: 'success', message: t('DetailsSaved') });
    } catch (err) {
      console.error(err);
      setFeedback({ type: 'danger', message: t('SaveFailed') });
    }
  }




  const [previews, setPreviews] = useState({
    right: { "Macula Map": null, "Macula Multi": null, "Fundus": null },
    left: { "Macula Map": null, "Macula Multi": null, "Fundus": null },
    both: { Both: null }
  });

  // 1) Keep your state
  const [selectedFilesByEye, setSelectedFilesByEye] = useState({
    right: { "Macula Map": null, "Macula Multi": null, "Fundus": null },
    left: { "Macula Map": null, "Macula Multi": null, "Fundus": null },
    both: { Both: null },
  });

  // 2) Build a true array of selections with unique keys
  const flatSelectedFiles = [];
  Object.entries(selectedFilesByEye).forEach(([eye, specs]) => {
    Object.entries(specs).forEach(([scanType, file]) => {
      if (file) {
        flatSelectedFiles.push({
          key: `${eye}-${scanType}`,  // "right-Macula Map", etc
          name: file.name
        });
      }
    });
  });


  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  function UploadCell({ selected, preview, onSelect, onDelete }) {
    return (
      <td className="p-2 text-center">
        {selected ? (
          <>
            {/* 1) Filename at the top */}
            <Form.Control
              readOnly
              value={selected.name}
              className="mb-1 text-truncate"
              title={selected.name}
            />

            {/* 2) Icons row underneath */}
            <div className="d-flex justify-content-center align-items-center gap-2">
              {/* ✕ delete */}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={onDelete}
                title="Remove"
                className="px-3"
              >
                ×
              </Button>

              {/* 🔍 preview */}
              {preview && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setPreviewUrl(preview);
                    setShowPreview(true);
                  }}
                  title="View"
                  className="px-3"
                >
                  🔍
                </Button>
              )}

              {/* ↻ re-upload */}
              <Form.Label
                className="btn btn-sm btn-outline-secondary mb-0 px-3"
                title="Re-upload"
              >
                ↻
                <Form.Control
                  type="file"
                  hidden
                  onChange={e => onSelect(e.target.files?.[0] || null)}
                />
              </Form.Label>
            </div>
          </>
        ) : (
          /* No file yet: just show “Choose” */
          <Form.Label className="btn btn-sm btn-outline-secondary d-block">
            {t("Choose")}
            <Form.Control
              type="file"
              hidden
              onChange={e => onSelect(e.target.files?.[0] || null)}
            />
          </Form.Label>
        )}
      </td>
    );
  }



  function pickFileFor(title, eye) {
    fileInputRef.current.click();         // reuse your existing file input
    fileInputRef.current.onchange = e => {
      const file = e.target.files[0];
      setSelectedFilesByEye(prev => ({
        ...prev,
        [eye]: { ...prev[eye], [title]: file }
      }));
    };
  }

  function removeFile(title, eye) {
    setSelectedFilesByEye(prev => {
      const copy = { ...prev[eye] };
      delete copy[title];
      return { ...prev, [eye]: copy };
    });
  }


  const openReturnedTasks = () => {
    const returned = tasks.filter(task => {
      const v = getCurrentVersion(task);
      if (v.status !== "Returned") return false;
      return currentUser.role === "doctor"
        ? v.acceptedBy === currentUser.id   // doctor’s own returns
        : task.uploadedBy === currentUser.id; // customer’s own returns
    });
    setReturnedModalTasks(returned);
    setShowReturnedModal(true);
  };

  // Push a new version to a task (only for specific actions)
  function pushNewVersion(task, overrides) {
    const oldVersion = getCurrentVersion(task);
    const newVersion = {
      ...oldVersion,
      ...overrides,
      updatedAt: new Date().toISOString(),
      files: overrides.files || oldVersion.files || task.files, // Handle files array
    };
    return {
      ...task,
      versions: [...task.versions, newVersion]
    };
  }

  // ------------------ Login Logic ------------------
  // In your login function (or in a useEffect that watches userRole):
  // Import getDoc from firebase/firestore if you haven't already:
  const handleLogin = async () => {
    if (!email || !password) {
      setFeedback({ type: "warning", message: "Please enter both email and password." });
      return;
    }

    setIsLoading(true); // Start loading

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userAuth = userCredential.user;
      // Fetch user data from Firestore...
      const userDocRef = doc(db, "users", userAuth.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        setLoggedIn(true);
        setUserRole(userData.role);
        setCurrentUser(userData);
        localStorage.setItem("loginTimestamp", Date.now().toString());
      } else {
        setFeedback({ type: "danger", message: "No user data found in Firestore." });
      }
    } catch (error) {
      console.error("Login error:", error);
      setFeedback({ type: "danger", message: error.message });
    } finally {
      // Hide the loading spinner after 2 seconds (simulate delay)
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  // ------------------ Logout ------------------
  const handleLogout = () => {
    auth.signOut().then(() => {
      // Clear your local state as needed.
      setLoggedIn(false);
      setShowSideMenu(false);
      setEmail("");
      setPassword("");
      setUserRole("");
      setCurrentUser(null);
      setFeedback({ type: 'info', message: "You have been logged out." });
    }).catch(error => {
      console.error("Error during sign out:", error);
    });
  };


  // ------------------ Doctor Portal Functions ------------------
  const handleAcceptTask = async (id) => {
    setIsProcessing(true);
    try {
      let updatedTask = null;
      setTasks(prev =>
        prev.map(task => {
          if (task.id === id) {
            updatedTask = pushNewVersion(task, {
              status: "Accepted by Doctor",
              acceptedBy: currentUser.id
            });
            return updatedTask;
          }
          return task;
        })
      );
      if (updatedTask) {
        await setDoc(doc(db, "tasks", id), updatedTask);
        setFeedback({ type: 'success', message: "Task accepted." });
        const targetUser = users.find(u => u.id === currentUser.id);
        if (targetUser) {
          const notifId = crypto.randomUUID();
          await setDoc(doc(db, 'notifications', notifId), {
            toUserId: targetUser.id,
            message: `📢 שלום ${updatedTask.name}, הפניה שלך עבור - - פוענחה OCT-Avnet`,
            createdAt: Timestamp.now(),
          });

          await sendNotificationMessage(targetUser, `📢 שלום ${updatedTask.name}, הפניה שלך עבור - - פוענחה OCT-Avnet`);
        }

      }
    } catch (err) {
      console.error(err);
      setFeedback({ type: 'danger', message: "Failed to accept task." });
    } finally {
      setIsProcessing(false);
    }
  };




  // Handle Download
  const handleDownloadFile = (fileUrl, fileName) => {
    window.open(fileUrl, '_blank');
  };


  const handleSaveUserSignature = async () => {
    if (!signaturePreview || !croppedAreaPixels || !userToSignId) return;
    setIsProcessing(true);

    try {
      // 1) Crop the image
      const croppedDataUrl = await getCroppedImg(
        signaturePreview,
        croppedAreaPixels
      );

      // 2) Convert to pure B/W on white background

      const bwDataUrl = await convertToBW(croppedDataUrl);

      // 3) Save to Firestore
      const userRef = doc(db, "users", userToSignId);
      await setDoc(userRef, { signature: bwDataUrl }, { merge: true });

      // update local state as before…
      // …

      setShowSignatureModal(false);
      setSignatureFile(null);
      setSignaturePreview(null);
      setUserToSignId(null);
      setFeedback({ type: "success", message: t("Signature saved successfully.") });
    } catch (err) {
      console.error(err);
      setFeedback({ type: "danger", message: t("Error saving signature. Please try again.") });
    } finally {
      setIsProcessing(false);
    }
  };
  const handleOpenUserSignatureModal = (userId) => {
    setUserToSignId(userId);
    setShowSignatureModal(true);
  };

  // Sign a task with the doctor's existing signature
  // Sign a task with the doctor's existing signature
  // Sign a task with the doctor's existing signature
  // Helper function to reverse a string (for RTL support)

  // Sign a task with the doctor's existing signature


  const handleSignTask = async (taskId) => {
    let fileName;
    let signedHtmlUrl;
    if (!currentUser || !currentUser.signature) {
      setFeedback({ type: 'danger', message: "No signature found for this user." });
      return;
    }

    // Find the task that needs to be signed.
    const currentTask = tasks.find(t => t.id === taskId);
    if (!currentTask) return;

    // Get the doctor's notes (using unsavedNotes if available)
    const currentNotes = unsavedNotes[taskId] !== undefined
      ? unsavedNotes[taskId]
      : getCurrentVersion(currentTask).doctorNotes;


    //  Prevent signing if notes are empty
    if (!currentNotes || !currentNotes.trim()) {
      setFeedback({
        type: 'warning',
        message: "Please enter doctor notes before completing the task."
      });
      return;
    }

    // Update the task locally (for immediate UI feedback)
    updateDoctorNotes(taskId, currentNotes);

    const doctorSignatureUrl = currentUser.signature;

    let customerLogoUrl = ""; // new
    try {
      const customerRef = doc(db, "users", currentTask.uploadedBy);
      const customerSnap = await getDoc(customerRef);
      if (customerSnap.exists()) {
        const data = customerSnap.data();
        customerLogoUrl = data.logoUrl || "";  // new
      }
    } catch (err) {
      console.error("Error fetching customer signature/logo", err);
    }

    const doctorNotes = currentNotes;
    const signatureDataUrl = currentUser.signature; // Assumed to be a data URL
    const signedFiles = [];

    // Determine language settings.
    const isHebrew = i18n.language === "he";
    const reportTitle = isHebrew ? "פיענוח OCT" : "Referral Report";
    const doctorLabel = isHebrew ? "שם" : "Name";
    const typeLabel = isHebrew ? "תאור" : "Doctor Type";
    const specializationLabel = isHebrew ? "התמחות" : "specialization";
    const licenseLabel = isHebrew ? "מספר רישיון" : "License Number";
    const patientIdLabel = isHebrew ? "תעודת זהות" : "Patient ID";
    const textDir = isHebrew ? "rtl" : "ltr";
    const PatientInformationLabel = isHebrew ? "פרטי המטופל" : "Patient Information";

    try {
      // Build a single HTML report.
      const htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>${reportTitle}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
           @page {
                size: A4;
                margin: 0mm 10mm 0mm 10mm; /* top, right, bottom, left */
              }
            body {
              font-family: 'Roboto', sans-serif;
              margin: 0;
              padding: 0;
              background-color: #f7f7f7;
              direction: ${textDir};
            }
            .report-container {
              position: relative;
              width: 210mm;
              heigth: 190mm;
              margin: 5mm auto;
              background-color: #fff;
              padding: 5mm;
              box-sizing: border-box;
              overflow-wrap: break-word; /* ensures super-long text wraps */
            }
            .header {
              text-align: center;
              border-bottom: 1mm solid #000;
              padding-bottom: 1mm;
              margin-bottom: 1mm;
              overflow-wrap: break-word; /* ensures super-long text wraps */
            }
            .logo-container {
              display: flex;
              justify-content: space-between;
              position: relative;
              height: 7mm;
              margin-bottom: 3mm;
            }
            .logo-container img {
              height: 7mm;
              width: auto;
            }
            .header h1 {
              font-size: 16pt;
              margin: 0;
              color: #ffc107;
            }
            .info {
              background-color: #ffc107;
              padding: 1mm;
              border-radius: 1mm;
              margin-bottom: 1mm;
              font-size: 12pt;
              color: #000;
              overflow-wrap: break-word; /* ensures super-long text wraps */
            }
            .info p {
              margin:1mm 0;
            }
            .content {
              font-size: 12pt;
              line-height: 1.5;
              color: #000;
              white-space: pre-wrap;
              margin-bottom: 1mm;
              overflow-wrap: break-word; /* ensures super-long text wraps */
              text-align: ${textDir === "rtl" ? "right" : "left"};
            }
            .signature {
              text-align: ${textDir === "rtl" ? "right" : "left"};
              margin-top: 1mm;
            }
            .signature img {
              width: 50mm;
              border: 0.5mm solid #000;
              padding: 1mm;
              background-color: #fff;
            }
            .footer {
              text-align: center;
              font-size: 10pt;
              color: #000;
              margin-top: 1mm;
            }
                 .signature-container {
            display: flex;
            justify-content: space-between;
            margin-top: 15px;
          }
              .signature-box img {
                max-width: 200px;
                max-height: 150px;
                object-fit: contain;
                padding: 1px;
              }
                .print-footer {
                text-align: center;
                padding: 1mm 0;
              }
            /* Print adjustments */
            @media print {
              .report-container {
                /* Set container width to printable width (A4 width - margins) */
                width: 190mm;
              }
                  .print-footer {
                  .print-footer {
                  position: fixed;
                  bottom: 0;
                  left: 0;
                  right: 0;
                  text-align: center;
                  /* Adjust padding/margins as needed */
                }
              .header {
                position: static !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="report-container">
            <div class="header">
              <p>${new Date().toLocaleString()}</p>
              <div class="logo-container">
                <img src="https://firebasestorage.googleapis.com/v0/b/optimizesignportal.firebasestorage.app/o/Group11.png?alt=media" alt="Logo 1" />
                <img src="https://firebasestorage.googleapis.com/v0/b/optimizesignportal.firebasestorage.app/o/Group9.png?alt=media" alt="Logo 2" />
              </div>
              <h1>${reportTitle}</h1>
            </div>
            <div class="info">
              <p><strong>${doctorLabel}:</strong> ${currentUser.username} ${currentUser.lastName}</p>
              <p><strong>${licenseLabel}:</strong> ${currentUser.licenseNumber}</p>
              <p><strong>${typeLabel}:</strong> ${currentUser.doctorType}</p>
              <p><strong>${specializationLabel}:</strong> ${currentUser.specialization}</p>
              <p><strong>${PatientInformationLabel}</strong></p>
              <p><strong>${patientIdLabel}:</strong> ${patientID || currentTask.patientID}</p>
            </div>
            <div class="content">
              ${doctorNotes}
            </div>
            <div class="print-footer">
            <div class="signature-container">
              <div class="signature-box">
               <img src="${customerLogoUrl}" 
                  alt="Customer Logo" 
                   style="
                      position: absolute; 
                      bottom: 20mm; 
                      left: 10mm; 
                      width: 4cm; 
                      height: 2cm; 
                      object-fit: contain;
                    " />
                <p><strong>${t('DoctorSignature')}:</strong></p>
                <img src="${doctorSignatureUrl}" />
                <p><strong>${t('LicenseNumber')}:</strong> ${currentUser.licenseNumber || "-"}</p>
              </div>
            </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

      // Create & upload HTML…

      const now = new Date();
      // e.g. “2025-06-19” and “10-35-08”
      const datePart = now.toISOString().split("T")[0];
      const timePart = now
        .toTimeString()
        .split(" ")[0]
        .replace(/:/g, "-");
      const blob = new Blob([htmlContent], { type: "text/html" });
      fileName = `Signed_${currentTask.name}_${datePart}_${timePart}.html`;                           // NEW
      const storageRef = ref(storage, `tasks/${taskId}/${fileName}`);
      await uploadBytes(storageRef, blob);
      signedHtmlUrl = await getDownloadURL(storageRef);                        // NEW

      signedFiles.push({
        id: uuidv4(),
        fileType: "text/html",
        fileUrl: signedHtmlUrl,
        fileName
      });

      // Update Firestore…
      const updatedTask = pushNewVersion(currentTask, {
        status: "Finished",
        files: [...getCurrentVersion(currentTask).files, ...signedFiles],
        finalDoctorNotes: doctorNotes,
        doctorNotes: "",
        customerViewed: false,
        signature: signatureDataUrl
      });
      await setDoc(doc(db, "tasks", updatedTask.id), updatedTask);

      setTasks(prev =>
        prev.map(t => t.id === taskId ? { ...updatedTask } : t)
      );
      setFeedback({ type: 'success', message: "Task signed and completed successfully." });
      const targetUser = users.find(u => u.id === currentUser.id);
      if (targetUser) {
        const notifId = crypto.randomUUID();
        await setDoc(doc(db, 'notifications', notifId), {
          toUserId: targetUser.id,
          message: `📢  ההפניה עבור ${updatedTask.name}, נסגרה ,קובץ פיענוח חתום הועלה בהצלחה למערכת.`,
          createdAt: Timestamp.now(),
        });

        await sendNotificationMessage(targetUser, `📢  ההפניה עבור ${updatedTask.name}, נסגרה ,קובץ פיענוח חתום הועלה בהצלחה למערכת.`);
      }


      return { signedHtmlUrl, fileName };                                      // NEW
    } catch (error) {
      console.error("Error generating or uploading signed HTML report:", error);
      setFeedback({ type: 'danger', message: "An error occurred while signing." });
      throw error;  // rethrow so delayed logic knows it failed
    }
  };

  const handleSaveDoctorNotes = async (taskId) => {
    const notes = unsavedNotes[taskId];
    if (!notes) {
      setFeedback({ type: 'warning', message: "No changes to save." });
      return;
    }

    const currentTask = tasks.find(t => t.id === taskId);
    if (!currentTask) return;

    const updatedTask = pushNewVersion(currentTask, {
      doctorNotes: notes,
      customerViewed: false,
      updatedAt: new Date().toISOString(),
    });

    try {
      await setDoc(doc(db, "tasks", updatedTask.id), updatedTask);
      setTasks(prevTasks => prevTasks.map(task => task.id === taskId ? updatedTask : task));
      setFeedback({ type: 'success', message: "Doctor notes saved." });

    } catch (error) {
      console.error("Error saving doctor notes:", error);
      setFeedback({ type: 'danger', message: "Failed to save notes." });
    }
  };

  const debouncedSaveDoctorNotes = debounce(async (taskId, notes) => {
    const currentTask = tasks.find(t => t.id === taskId);
    if (!currentTask) return;

    const updatedTask = pushNewVersion(currentTask, {
      doctorNotes: notes,
      customerViewed: false,
      updatedAt: new Date().toISOString(),
    });

    try {
      await setDoc(doc(db, "tasks", updatedTask.id), updatedTask);
      setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t));

    } catch (error) {

      setFeedback({ type: 'danger', message: "Auto-save failed." });
    }
  }, 1000); // wait 1 second after typing stops



  // Make sure to import jsPDF and (optionally) autoTable.
  // If you installed autoTable via npm, you might do:

  // Customer‐side report
  const generateTaskReportPDFCustomer = (
    startDate = null,
    endDate = null,
    status = null,
    doctorId = null
  ) => {
    if (!currentUser) {
      setFeedback({
        type: "warning",
        message: "You must be logged in to generate a report.",
      });
      return;
    }

    // 1) Only this customer's tasks
    const customerTasks = tasks.filter(
      (task) => task.uploadedBy === currentUser.id
    );

    // 2) Apply filters...
    const filteredTasks = customerTasks.filter((task) => {
      const current = getCurrentVersion(task);
      if (status && status !== "All") {
        const ts = (current.status || "").trim().toLowerCase();
        if (ts !== status.trim().toLowerCase()) return false;
      }
      if (startDate && new Date(current.updatedAt) < new Date(startDate))
        return false;
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (new Date(current.updatedAt) > end) return false;
      }
      if (doctorId && current.acceptedBy !== doctorId) return false;
      return true;
    });

    // 3) Build the PDF
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Monthly Task Report", 20, 20);
    doc.setFontSize(12);
    doc.text(`Total Tasks: ${filteredTasks.length}`, 20, 30);

    if (doctorId) {
      const d = users.find((u) => u.id === doctorId);
      if (d) doc.text(`Doctor: ${d.username} ${d.lastName}`, 20, 40);
    }

    // 4) Table data
    const cols = [
      "Doctor Name",
      "Patient Name",
      "Patient ID",
      "Finish Date",
      "Finish Time",
    ];
    const rows = filteredTasks.map((task) => {
      const current = getCurrentVersion(task);
      const docUser = users.find((u) => u.id === current.acceptedBy) || {};
      const patient = users.find((u) => u.id === task.uploadedBy) || {};

      const dt = new Date(current.updatedAt);
      return [
        `${docUser.username || ""} ${docUser.lastName || ""}`.trim(),
        `${patient.username || ""} ${patient.lastName || ""}`.trim(),
        task.patientID || "",
        dt.toLocaleDateString(),
        dt.toLocaleTimeString(),
      ];
    });

    autoTable(doc, {
      startY: 50,
      head: [cols],
      body: rows,
      theme: "grid",
      headStyles: { fillColor: [22, 160, 133] },
      styles: { fontSize: 10 },
    });

    // Footer & save
    const h = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, h - 10);
    doc.save(
      `Customer_Report_${currentUser.username}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`
    );
  };

  // Doctor‐side report
  const generateTaskReportPDFDoc = (
    customerId = null,
    startDate = null,
    endDate = null
  ) => {
    if (!currentUser) {
      alert("You must be logged in to generate a report.");
      return;
    }

    // 1) filter tasks...
    const filteredTasks = tasks.filter((task) => {
      const current = getCurrentVersion(task);
      if (customerId && task.uploadedBy !== customerId) return false;
      if (current.acceptedBy !== currentUser.id) return false;
      if (
        selectedStatus !== "All" &&
        (current.status || "").trim().toLowerCase() !==
        selectedStatus.toLowerCase()
      )
        return false;
      if (startDate && new Date(current.updatedAt) < new Date(startDate))
        return false;
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (new Date(current.updatedAt) > end) return false;
      }
      return true;
    });

    // 2) prep the doc
    const doc = new jsPDF();
    const name = customerId
      ? users.find((u) => u.id === customerId)?.username || customerId
      : "All Customers";

    let title = `Doctor's Finished Tasks Report for ${name}`;
    if (startDate || endDate) {
      title += ` (${startDate || "..."} - ${endDate || "..."})`;
    }
    doc.setFontSize(18);
    doc.text(doc.splitTextToSize(title, 180), 20, 20);

    doc.setFontSize(12);
    doc.text(`Total Finished Tasks: ${filteredTasks.length}`, 20, 40);

    // 3) Table data
    const tableColumn = [
      "Store Name",
      "Task Name",
      "Finish Date",
      "Finish Time",
      "Patient ID Number",
    ];
    const tableRows = filteredTasks.map((task) => {
      const current = getCurrentVersion(task);
      const store =
        users.find((u) => u.id === task.uploadedBy)?.storeName || "";
      const taskName = task.name || current.taskName || "";
      const dt = new Date(current.updatedAt);
      const patientID = task.patientID || "";

      return [
        store,
        taskName,
        dt.toLocaleDateString(),
        dt.toLocaleTimeString(),
        patientID,
      ];
    });

    autoTable(doc, {
      startY: 50,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 10 },
    });

    // footer
    const ph = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.text(`Report generated: ${new Date().toLocaleString()}`, 20, ph - 10);

    doc.save(`Doctor_Report_${name.replace(/\s+/g, "_")}.pdf`);
  };


  const handleReturnTask = (id) => {
    setReturningTaskId(id);
    setShowReturnTaskModal(true);
    setReturnReason("");
  };


  const returnTask = async (id) => {
    console.log("▶️ returnTask() called with:", { id, returnReason });

    // 1) Find the original task
    const original = tasks.find(t => t.id === id);
    if (!original) {
      console.warn("⚠️ No task found with id:", id);
      return;
    }
    console.log("• original task:", original);

    // 2) Build the updated version
    const newVersion = pushNewVersion(original, {
      status: "Returned",
      customerReturnViewed: false,
      returnReason, // from state
      doctorNotes: original.versions[original.versions.length - 1].doctorNotes
    });
    console.log("• newVersion:", newVersion);

    // 3) Update React state immediately
    setTasks(prev =>
      prev.map(t => t.id === id ? newVersion : t)
    );

    // 4) Close modal & show UI feedback
    setShowReturnTaskModal(false);
    setFeedback({ type: 'warning', message: "Task has been returned." });

    // 5) Write to Firestore
    try {
      console.log("💾 Writing task to Firestore at path tasks/" + id);
      await setDoc(doc(db, "tasks", id), newVersion);
      console.log("✅ Firestore write succeeded for task:", id);
    } catch (err) {
      console.error("❌ Firestore write failed:", err);
      return;
    }

    // 6) Notify the customer (uploader)
    const uploader = users.find(u => u.id === newVersion.uploadedBy);
    if (uploader?.phoneNumber) {
      const custMsg = `📢 שלום ${uploader.username}, המשימה "${newVersion.name}" הוחזרה לבדיקת רופא.`;
      console.log("✉️ Notifying customer:", custMsg);
      const custNotifId = crypto.randomUUID();
      await setDoc(doc(db, 'notifications', custNotifId), {
        toUserId: uploader.id,
        message: custMsg,
        createdAt: Timestamp.now(),
      });
      await sendNotificationMessage(uploader, custMsg);
    } else {
      console.warn("⚠️ No phone number for uploader:", uploader);
    }

    // 7) Notify the doctor
    const mainDoc = users.find(u => u.id === uploader.mainDoctor);
    const secDoc = users.find(u => u.id === uploader.secondaryDoctor);
    const target = mainDoc?.isAvailable ? mainDoc : secDoc;
    if (target?.phoneNumber) {
      const docMsg = `📢 הרופא החזיר את המשימה"${newVersion.name}"  עבורך לבדיקת המשך.`;
      console.log("✉️ Notifying doctor:", docMsg);
      const docNotifId = crypto.randomUUID();
      await setDoc(doc(db, 'notifications', docNotifId), {
        toUserId: target.id,
        message: docMsg,
        createdAt: Timestamp.now(),
      });
      await sendNotificationMessage(target, docMsg);
    } else {
      console.warn("⚠️ No available doctor to notify");
    }
  };





  // ------------------ Customer Portal Functions ------------------
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      const validFiles = [];
      const invalidFiles = [];

      filesArray.forEach(file => {
        const maxSizeInBytes = 10 * 1024 * 1024; // 5MB
        if (file.size > maxSizeInBytes) {
          invalidFiles.push(`${file.name} exceeds the 5MB limit.`);
          return;
        }

        const allowedTypes = ["application/pdf", "image/jpeg"];
        if (!allowedTypes.includes(file.type)) {
          invalidFiles.push(`${file.name} is not an allowed file type.`);
          return;
        }

        validFiles.push({ id: uuidv4(), file });
      });

      if (invalidFiles.length > 0) {
        setFeedback({ type: 'danger', message: invalidFiles.join(' ') });
      }

      if (validFiles.length > 0) {
        setSelectedFiles(prevFiles => [...prevFiles, ...validFiles]);
        setStagedFiles(validFiles);
        setShowMetaModal(true);
        setFeedback({ type: 'success', message: `${validFiles.length} file(s) selected.` });
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  };

  const handleFileUpload = async () => {
    // 1) Prevent double‐clicks
    if (uploading) return;
    setUploading(true);

    // 2) Validate required fields
    if (!patientID.trim() || !age.toString().trim() || !customTaskName.trim()) {
      setFeedback({
        type: 'warning',
        message: "Please fill in all required fields: Patient Name, ID and Age."
      });
      setUploading(false);
      return;
    }

    // 3) Flatten table‐based file selections
    const toUpload = [];
    Object.entries(selectedFilesByEye).forEach(([eye, specs]) => {
      Object.entries(specs).forEach(([scanType, file]) => {
        if (file) toUpload.push({ eye, scanType, file });
      });
    });
    if (!toUpload.length) {
      setFeedback({
        type: 'warning',
        message: "Please select at least one scan to upload!"
      });
      setUploading(false);
      return;
    }

    try {
      // 4) (Optional) simulate a short delay
      await new Promise(res => setTimeout(res, 3000));

      // 5) Build new task metadata
      const customerFullName = `${currentUser.username} ${currentUser.lastName}`;
      const taskNumber = tasks.filter(t => t.uploadedBy === currentUser.id).length + 1;
      const newTaskName = customTaskName || `${customerFullName} Task #${taskNumber}`;
      const newTaskId = uuidv4();

      // 6) Upload each file and track progress
      const uploadedFiles = [];
      for (let i = 0; i < toUpload.length; i++) {
        const { eye, scanType, file } = toUpload[i];
        const downloadURL = await uploadFileToStorage(
          file,
          `${newTaskId}/${eye}/${scanType}/${file.name}`
        );
        uploadedFiles.push({
          id: uuidv4(),
          fileType: file.type,
          fileUrl: downloadURL,
          fileName: file.name,
          eye,
          customTitle: scanType
        });
        setUploadProgress(Math.round(((i + 1) / toUpload.length) * 100));
      }

      // 7) Compose and persist the new task
      const newTask = {
        id: newTaskId,
        name: newTaskName,
        uploadedBy: currentUser.id,
        storeName: currentUser.storeName || "N/A",
        patientID,
        age,
        diseases,
        taskNotes,
        files: uploadedFiles,
        versions: [
          {
            status: "Not Accepted",
            doctorNotes: "",
            finalDoctorNotes: "",
            returnReason: "",
            updatedAt: new Date().toISOString(),
            files: uploadedFiles
          }
        ]
      };

      // 8) Notify the appropriate doctor
      const customer = users.find(u => u.id === currentUser.id);
      const mainDoctor = users.find(u => u.id === customer?.mainDoctor);
      const secondaryDoctor = users.find(u => u.id === customer?.secondaryDoctor);
      const targetDoctor = mainDoctor?.isAvailable ? mainDoctor : secondaryDoctor;
      if (targetDoctor) {
        const notifId = crypto.randomUUID();
        await setDoc(doc(db, 'notifications', notifId), {
          toUserId: targetDoctor.id,
          message: "📢 משימה חדשה הועלתה עבורך למערכת לבדיקה.",
          createdAt: Timestamp.now(),
        });
        await sendNotificationMessage(
          targetDoctor,
          "📢 משימה חדשה הועלתה עבורך למערכת לבדיקה."
        );
      }
      await setDoc(doc(db, "tasks", newTask.id), newTask);
      setFeedback({ type: 'success', message: "Files uploaded successfully." });

      // 9) Reset only the file‐picker table
      setSelectedFilesByEye({
        right: { "Macula Map": null, "Macula Multi": null, Fundus: null },
        left: { "Macula Map": null, "Macula Multi": null, Fundus: null },
        both: { Both: null }
      });
      // 10) Clear form inputs
      setCustomTaskName("");
      setPatientID("");
      setAge("");
      setDiseases("");
      setTaskNotes("");
      // 11) Reset progress bar
      setUploadProgress(0);

    } catch (error) {
      console.error("Upload failed:", error);
      setFeedback({ type: 'danger', message: "Upload failed. Please try again." });
    } finally {
      // 12) Always stop the spinner
      setUploading(false);
    }
  };



  // Re-upload a task (Enhancement 1 and 3)
  const handleReupload = (taskId) => {
    setReuploadTaskId(taskId);
    setReuploadFile(null);
    setShowTaskDetail(false);
    setShowReuploadModal(true);
  };

  const confirmReupload = async () => {
    if (!reuploadTaskId || !reuploadFile) {
      setFeedback({ type: 'warning', message: "Please select a file to reupload." });
      return;
    }

    const newFileUrl = URL.createObjectURL(reuploadFile.file);

    let updatedTask = null; // Define updatedTask outside of the map function

    setTasks((prevTasks) =>
      prevTasks.map((task) => {
        if (task.id === reuploadTaskId) {
          const newTask = pushNewVersion(task, {
            files: [
              ...getCurrentVersion(task).files,
              {
                id: reuploadFile.id,
                fileType: reuploadFile.file.type,
                fileUrl: newFileUrl,
                fileName: reuploadFile.file.name,
              }
            ],
            status: "Not Accepted",
            updatedAt: new Date().toISOString(),
          });
          updatedTask = newTask; // Update updatedTask
          return newTask;
        }
        return task;
      })
    );

    setShowReuploadModal(false);
    setReuploadTaskId(null);
    setReuploadFile(null);
    setFeedback({ type: 'success', message: "Task reuploaded successfully." });

    // FIRESTORE: Write updatedTask to Firestore
    if (updatedTask) {
      await setDoc(doc(db, "tasks", updatedTask.id), updatedTask);
      const customer = users.find(u => u.id === updatedTask.uploadedBy);
      if (!customer) return;

      const mainDoctor = users.find(u => u.id === customer.mainDoctor);
      const secondaryDoctor = users.find(u => u.id === customer.secondaryDoctor);

      let targetDoctor = null;
      if (mainDoctor?.isAvailable) {
        targetDoctor = mainDoctor;
      } else if (secondaryDoctor) {
        targetDoctor = secondaryDoctor;
      }

      if (targetDoctor) {
        const notifId = crypto.randomUUID();
        await setDoc(doc(db, 'notifications', notifId), {
          toUserId: targetDoctor.id,
          message: "📢 משימה חדשה הועלתה עבורך למערכת לבדיקה.",
          createdAt: Timestamp.now(),
        });

        await sendNotificationMessage(targetDoctor, `📢 משימה חדשה הועלתה עבורך למערכת לבדיקה.`);
      }

    }
  };

  // ------------------ Admin Portal Functions ------------------

  // update fields in the *Edit User* form
  const handleEditUserDataChange = (e) => {
    const { name, value } = e.target;
    setEditUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Create user handler with role-based required-field check
  const handleCreateUser = async () => {
    // 1) Ensure email & password are provided
    if (!userData.email || !userData.password) {
      setFeedback({
        type: 'warning',
        message: t('PleaseEnterEmailAndPassword')
      });
      return;
    }

    try {
      // 2) Create the auth user on your secondary instance
      const { user: newUserAuth } = await createUserWithEmailAndPasswordSecondary(
        secondaryAuth,
        userData.email,
        userData.password
      );

      // 3) Optionally upload a license file
      let licenseFileUrl = '';
      if (licenseFile) {
        const licenseRef = ref(
          storage,
          `licenses/${newUserAuth.uid}/${licenseFile.name}`
        );
        await uploadBytes(licenseRef, licenseFile);
        licenseFileUrl = await getDownloadURL(licenseRef);
      }

      // 4) Compute specialization text if “other”
      const finalSpecialization =
        userData.role === 'doctor'
          ? (userData.specialization === 'other'
            ? userData.otherSpecialization
            : userData.specialization)
          : '';

      // 5) Build the Firestore payload
      const newUser = {
        id: newUserAuth.uid,
        username: userData.username,
        lastName: userData.lastName,
        role: userData.role,
        email: userData.email,
        idNumber: userData.idNumber,
        licenseNumber: userData.licenseNumber,
        address: userData.address,
        PhoneNumber: userData.PhoneNumber || '',
        storeName: userData.role === 'customer' ? userData.storeName : '',
        mainDoctor: userData.role === 'customer' ? userData.mainDoctor : '',
        secondaryDoctor: userData.role === 'customer' ? userData.secondaryDoctor : '',
        anydeskuser: userData.anydeskuser || '',
        anydeskpassword: userData.anydeskpassword || '',
        doctorType: userData.role === 'doctor' ? userData.doctorType : '',
        specialization: finalSpecialization,
        otherSpecialization: userData.otherSpecialization,
        licenseFileUrl,
        signature: userData.role === 'doctor' ? '' : null,
        signedPdf: ''
      };

      // 6) Save to Firestore and tear down secondary auth
      await setDoc(doc(db, 'users', newUserAuth.uid), newUser);
      await signOutSecondary(secondaryAuth);

      // 7) Reset form & show success
      setFeedback({ type: 'success', message: t('UserCreated') });
      setUserData({
        username: '',
        lastName: '',
        email: '',
        password: '',
        idNumber: '',
        licenseNumber: '',
        address: '',
        PhoneNumber: '',
        role: 'customer',
        storeName: '',
        doctorType: 'Doctor',
        specialization: '',
        otherSpecialization: '',
        mainDoctor: '',
        secondaryDoctor: '',
        anydeskuser: '',
        anydeskpassword: ''
      });
    } catch (error) {
      // 8) Handle known errors
      if (error.code === 'auth/email-already-in-use') {
        setFeedback({ type: 'danger', message: t('EmailInUse') });
      } else if (error.code === 'auth/weak-password') {
        setFeedback({ type: 'danger', message: t('WeakPassword') });
      } else if (error.code === 'auth/invalid-email') {
        setFeedback({ type: 'danger', message: t('InvalidEmail') });
      } else {
        console.error('Registration error:', error);
        setFeedback({ type: 'danger', message: error.message });
      }
    }
  };



  const handleUserDataChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  // Editing existing user
  // When you open the Edit Modal
  const openEditUserModal = async (userId) => {
    setEditingUserId(userId);
    const userToEdit = users.find(u => u.id === userId);
    if (!userToEdit) return;


    const isPre = PREDEFINED_SPECS.includes(userToEdit.specialization);

    setEditUserData({
      username: userToEdit.username,
      lastName: userToEdit.lastName,
      email: userToEdit.email,
      idNumber: userToEdit.idNumber || "",
      licenseNumber: userToEdit.licenseNumber || "",
      address: userToEdit.address || "",
      role: userToEdit.role,
      // Do NOT copy userToEdit.password
      // We’re removing that for admin
      password: "",   // or null
      doctorType: userToEdit.doctorType || "",
      specialization: isPre
        ? userToEdit.specialization
        : "other",
      otherSpecialization: isPre
        ? ""
        : userToEdit.specialization,
      storeName: userToEdit.storeName || "",
      mainDoctor: userToEdit.mainDoctor || "",
      secondaryDoctor: userToEdit.secondaryDoctor || "",
      signature: userToEdit.signature || null,
      licenseFileUrl: "",
      anydeskuser: userToEdit.anydeskuser || "",
      anydeskpassword: userToEdit.anydeskpassword || "",
      PhoneNumber: userToEdit.PhoneNumber || ""

    });
    setShowEditUserModal(true);
  };

  const saveEditedUser = async () => {



    // Validate required fields
    // Example check
    if (!editUserData.username || !editUserData.lastName || !editUserData.email) {
      setFeedback({ type: 'warning', message: "Please fill in all required fields." });
      return;
    }

    // Check for duplicate email (excluding the user being edited)
    if (users.some(u => u.email === editUserData.email && u.id !== editingUserId)) {
      setFeedback({ type: 'danger', message: "Another user with this email already exists." });
      return;
    }

    const finalSpecialization = editUserData.role === "doctor"
      ? (editUserData.specialization === "other"
        ? editUserData.otherSpecialization
        : editUserData.specialization)
      : "";

    // Build the updated user object
    const updatedUser = {
      username: editUserData.username,
      lastName: editUserData.lastName,
      email: editUserData.email,
      idNumber: editUserData.idNumber,
      licenseNumber: editUserData.licenseNumber,
      address: editUserData.address,
      role: editUserData.role,
      ...(editUserData.password && currentUser?.role !== "admin" ? { password: editUserData.password } : {}),
      signature: editUserData.signature,
      doctorType: editUserData.role === "doctor" ? editUserData.doctorType : "",
      storeName: editUserData.role === "customer" ? editUserData.storeName : "",
      mainDoctor: editUserData.role === "customer" ? editUserData.mainDoctor : "",
      secondaryDoctor: editUserData.role === "customer" ? editUserData.secondaryDoctor : "",
      specialization: finalSpecialization,
      anydeskuser: editUserData.role === "customer" ? editUserData.anydeskuser : "",
      anydeskpassword: editUserData.role === "customer" ? editUserData.anydeskpassword : "",
      PhoneNumber: editUserData.PhoneNumber,
    };

    try {
      // Update Firestore
      const userDocRef = doc(db, "users", editingUserId);
      await updateDoc(doc(db, "users", editingUserId), updatedUser);

      // Optionally update your local state (if you rely on Firestore onSnapshot,
      // this may update automatically after the change)
      setUsers(prevUsers =>
        prevUsers.map(u => (u.id === editingUserId ? { ...u, ...updatedUser } : u))
      );
      setShowEditUserModal(false);
      setEditingUserId(null);
      setFeedback({ type: 'success', message: "User details updated successfully." });
    } catch (error) {
      console.error("Error updating user in Firestore:", error);
      setFeedback({ type: 'danger', message: "Error updating user details. Please try again." });
    }
  };

  // ------------------ Filtering, Searching, and Sorting ------------------
  // Compute the tasks that the current user is allowed to see:
  // Compute tasks that the current user is allowed to see:
  const visibleTasks = tasks.filter(task => {
    if (!currentUser) return false;
    const currentVersion = getCurrentVersion(task);

    if (currentUser.role === "customer") {
      return task.uploadedBy === currentUser.id;
    }

    if (currentUser.role === "doctor") {
      const uploader = users.find(u => u.id === task.uploadedBy);
      if (!uploader || uploader.role !== "customer") return false;

      const mainDoctorId = uploader.mainDoctor;
      const secondaryDoctorId = uploader.secondaryDoctor;
      const mainDoctor = users.find(u => u.id === mainDoctorId);

      const isMain = currentUser.id === mainDoctorId;
      const isSecondary = currentUser.id === secondaryDoctorId;

      // 1. Not Accepted → decide dynamically
      if (currentVersion.status === "Not Accepted") {
        if (isMain && mainDoctor?.isAvailable) return true;
        if (isSecondary && (!mainDoctor || !mainDoctor.isAvailable)) return true;
        return false;
      }

      // 2. Accepted or Finished → show only to assigned doctor
      if (
        (currentVersion.status === "Accepted by Doctor" || currentVersion.status === "Finished") &&
        currentVersion.acceptedBy === currentUser.id
      ) {
        return true;
      }

      return false;
    }

    // Admin sees everything
    if (currentUser.role === "admin") return true;

    return false;
  });

  const mainDoc = currentUser?.mainDoctor
    ? users.find(u => u.id === currentUser.mainDoctor)
    : null;
  const secondaryDoc = currentUser?.secondaryDoctor
    ? users.find(u => u.id === currentUser.secondaryDoctor)
    : null;



  // Then, apply search filtering and status filtering on visibleTasks:
  const filteredTasks = visibleTasks.filter(task => {
    const searchLower = searchTerm.toLowerCase();
    const taskName = task.name ? task.name.toLowerCase() : "";
    const patientIDVal = task.patientID ? task.patientID.toLowerCase() : "";
    const ageVal = task.age ? task.age.toString().toLowerCase() : "";
    const diseasesVal = task.diseases ? task.diseases.toLowerCase() : "";
    const doctor = task.acceptedBy ? task.acceptedBy.toLowerCase() : "";

    const fileMatches = task.files && task.files.some(file =>
      (file.fileName ? file.fileName.toLowerCase() : "").includes(searchLower)
    );

    return (
      (selectedStatus === "All" || getCurrentVersion(task).status === selectedStatus) &&
      (taskName.includes(searchLower) ||
        patientIDVal.includes(searchLower) ||
        ageVal.includes(searchLower) ||
        diseasesVal.includes(searchLower) ||
        doctor.includes(searchLower) ||
        fileMatches)
    );
  });


  // Finally, sort the filtered tasks:
  const sortedTasks = filteredTasks.sort((a, b) => {
    const dateA = new Date(getCurrentVersion(a).updatedAt);
    const dateB = new Date(getCurrentVersion(b).updatedAt);
    return sortOrder === "Newest First" ? dateB - dateA : dateA - dateB;
  });


  function getStatusColor(status) {
    switch (status) {
      case "Returned":
        return "red";
      case "Finished":
        return "green";
      case "Accepted by Doctor":
        return "orange";
      default:
        return "inherit";
    }
  }

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  function openHistory(task) {
    setHistoryTask(task);
    setShowHistoryModal(true);
  }


  const handleDeleteUser = (userId) => {
    setUserToDelete(userId);
    setShowDeleteUser(true);
  };

  // Function to delete user from Firestore and Firebase Authentication


  const confirmDeleteUser = async () => {
    // close the confirmation modal
    setShowDeleteUser(false);

    try {
      // 1) grab a fresh ID token
      const auth = getAuth();
      await auth.currentUser.getIdToken(true);  // <-- force refresh
      const idToken = await auth.currentUser.getIdToken();


      // 2) call your Cloud Function deleteUser route
      const res = await fetch(
        "https://us-central1-optimizesignportal.cloudfunctions.net/api/deleteUser",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ uid: userToDelete }),
        }
      );

      // 3) check for errors
      const payload = await res.json();
      if (!res.ok || !payload.success) {
        throw new Error(payload.error || "Delete failed");
      }

      // 4) update local state
      setUsers((us) => us.filter((u) => u.id !== userToDelete));
      setFeedback({ type: "success", message: t("UserDeleted") });
    } catch (err) {
      console.error(err);
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setUserToDelete(null);
    }
  };

  const colWidths = ["25%", "25%", "25%", "25%"];

  const cancelDeleteUser = () => {
    setShowDeleteUser(false);
    setUserToDelete(null);
  };


  /*delete task */
  // Opens the modal and stores the task ID
  const handleDeleteTask = (taskId) => {

    setTaskToDelete(taskId);
    setShowDeleteTaskModal(true);
  };

  // Closes the modal and clears the stored task ID
  const cancelDeleteTask = () => {
    setShowDeleteTaskModal(false);
    setTaskToDelete(null);
  };

  // Confirms deletion: update local state and (simulate) Firestore deletion
  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;

    try {
      // 1) Delete from Firestore
      await deleteDoc(doc(db, "tasks", taskToDelete));

      // 2) Remove from local state
      setTasks(prev => prev.filter(t => t.id !== taskToDelete));

      // 3) Close modal & clear
      setShowDeleteTaskModal(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error("Failed to delete task:", error);
      setFeedback({ type: "danger", message: "Error deleting task." });
    }
  };


  // Delete file from a task – using the browser's confirmation dialog
  const handleDeleteFile = async (task, fileId) => {
    // Use the browser's confirmation dialog
    const confirmed = window.confirm(
      "האם אתה בטוח שרוצה למחוק את הקובץ?"
    );
    if (!confirmed) return;

    // Update the local state: remove the file from both the task's main file array and the latest version's files
    const updatedTasks = tasks.map((t) => {
      if (t.id === task.id) {
        const updatedFiles = (t.files || []).filter((f) => f.id !== fileId);
        const updatedVersions = t.versions.map((version, index) =>
          index === t.versions.length - 1
            ? { ...version, files: (version.files || []).filter((f) => f.id !== fileId) }
            : version
        );
        return { ...t, files: updatedFiles, versions: updatedVersions };
      }
      return t;
    });
    setTasks(updatedTasks);
    setFeedback({ type: "success", message: "File deleted successfully." });

    // Update Firestore with the updated task data
    const updatedTask = updatedTasks.find((t) => t.id === task.id);
    if (updatedTask) {
      try {
        await setDoc(doc(db, "tasks", updatedTask.id), updatedTask);
      } catch (error) {
        console.error("Error updating task in Firestore after file deletion:", error);
        setFeedback({ type: "danger", message: "Error updating task in Firestore." });
      }
    }
    // Optionally, close the Task Detail modal after deletion:
    setShowTaskDetail(false);
  };




  useEffect(() => {
    if (feedback.message) {
      const timer = setTimeout(() => {
        setFeedback({ type: '', message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);


  useEffect(() => {
    if (!enableReminderSMS || reminderDelayHours < 1) return;

    const handle = setInterval(async () => {
      const cutoff = Date.now() - reminderDelayHours * 3600 * 1000;

      const tasksByDoctor = {};
      tasks.forEach(task => {
        const v = getCurrentVersion(task);
        if (v.status === "Accepted by Doctor") {
          const acceptedAt = new Date(v.updatedAt).getTime();
          if (acceptedAt < cutoff) {
            tasksByDoctor[v.acceptedBy] = tasksByDoctor[v.acceptedBy] || [];
            tasksByDoctor[v.acceptedBy].push(task);
          }
        }
      });

      for (const [docId, overdueTasks] of Object.entries(tasksByDoctor)) {
        const doctor = users.find(u => u.id === docId);
        if (!doctor) continue;
        const msg = t(
          "You have {{count}} open tasks awaiting your review.",
          { count: overdueTasks.length }
        );
        await sendNotificationMessage(doctor, msg);
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(handle);
  }, [enableReminderSMS, reminderDelayHours, tasks, users]);


  const viewSignedNotes = (task) => {
    const finishedVersion = task.versions.find(version => version.status === "Finished");
    if (finishedVersion && finishedVersion.files.length > 0) {
      const signedPdf = finishedVersion.files.find(file => file.fileType === "application/pdf");
      if (signedPdf) {
        setSignedNotesFile(signedPdf);
        setShowSignedNotesModal(true);
      } else {
        setFeedback({ type: 'warning', message: "No signed PDF found for this task." });
      }
    } else {
      setFeedback({ type: 'warning', message: "No finished version found for this task." });
    }
  };

  const [unsavedNotes, setUnsavedNotes] = useState({});

  const updateDoctorNotes = (taskId, newNotes) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId
          ? {
            ...task,
            versions: task.versions.map((version, index) =>
              index === task.versions.length - 1
                ? { ...version, doctorNotes: newNotes }
                : version
            )
          }
          : task
      )
    );
  };

  const handleLanguageAndClose = (checked) => {
    i18n.changeLanguage(checked ? "he" : "en");
    setShowSideMenu(false);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setShowSideMenu(false),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });


  const handleForgotPassword = async () => {
    if (!email) {
      setFeedback({ type: 'warning', message: "Please enter your email address first." });
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setFeedback({ type: 'success', message: `A password reset link has been sent to ${email}.` });
    } catch (error) {
      console.error("Failed to send reset link:", error);
      setFeedback({ type: 'danger', message: "Failed to send reset link. Check your email and try again." });
    }
  };

  const handleLogoFileChange = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];

    try {
      // 1) First, shrink it client-side to 600×300 px (white background, contain)
      const smallDataUrl = await resizeImageFile(file, 600, 300);

      // 2) Turn the Data-URL back into a Blob so Firebase Storage will accept it
      const response = await fetch(smallDataUrl);
      const blob = await response.blob();
      // you can preserve original filename or give a new one:
      const filename = file.name.replace(/\.\w+$/, '') + "_600x300.png";

      // 3) Upload to Firebase Storage at `logos/${currentUser.id}/${filename}`
      const storageRef = ref(storage, `logos/${currentUser.id}/${filename}`);
      await uploadBytes(storageRef, blob);

      // 4) Get the download URL
      const downloadURL = await getDownloadURL(storageRef);

      // 5) Save this URL into the user doc in Firestore
      const userDocRef = doc(db, "users", currentUser.id);
      await updateDoc(userDocRef, { logoUrl: downloadURL });

      // 6) Update local state so the UI shows the new, resized logo
      setCurrentUser((prev) => ({ ...prev, logoUrl: downloadURL }));

      setFeedback({ type: "success", message: "Logo uploaded successfully." });
    } catch (err) {
      console.error("Error uploading logo:", err);
      setFeedback({ type: "danger", message: "Failed to upload logo." });
    }
  };


  const newSignedCount = useMemo(() => {
    if (!currentUser) return 0;
    return tasks.filter(t => {
      const v = getCurrentVersion(t);
      return (
        t.uploadedBy === currentUser.id &&
        v.status === "Finished" &&
        !v.customerViewed
      );
    }).length;
  }, [tasks, currentUser]);


  // in your App() component, alongside newSignedCount…
  const newReturnedCount = useMemo(() => {
    if (!currentUser) return 0;
    return tasks.filter(t => {
      const v = getCurrentVersion(t);
      return (
        t.uploadedBy === currentUser.id &&
        v.status === "Returned" &&
        !v.customerReturnViewed
      );
    }).length;
  }, [tasks, currentUser]);



  const newTaskCount = useMemo(() => {
    if (!currentUser || currentUser.role !== 'doctor') return 0;

    return tasks.filter(task => {
      const currentVersion = getCurrentVersion(task);
      if (currentVersion.status !== 'Not Accepted') return false;

      const uploader = users.find(u => u.id === task.uploadedBy);
      if (!uploader || uploader.role !== 'customer') return false;

      const mainDoctorId = uploader.mainDoctor;
      const secondaryDoctorId = uploader.secondaryDoctor;
      const mainDoctor = users.find(u => u.id === mainDoctorId);

      const isMain = currentUser.id === mainDoctorId;
      const isSecondary = currentUser.id === secondaryDoctorId;

      if (isMain && mainDoctor?.isAvailable) return true;
      if (isSecondary && (!mainDoctor || !mainDoctor.isAvailable)) return true;

      return false;
    }).length;
  }, [tasks, currentUser, users]);



  /**
   * @param {string} taskId
   * @param {'Finished'|'Returned'} type
   */
  async function markAsViewed(taskId, type) {
    // pick the right flag
    const flag = type === 'Finished'
      ? 'customerViewed'
      : 'customerReturnViewed';

    // find the task object
    const task = tasks.find(t => t.id === taskId);
    if (!task || !Array.isArray(task.versions)) return;

    // locate the version index that matches the status we want to mark
    const idx = task.versions.findIndex(v => v.status === type);
    if (idx < 0) return;  // nothing to do if no version matches

    // build a new versions array, updating only that one version
    const newVersions = task.versions.map((v, i) =>
      i === idx
        ? { ...v, [flag]: true }
        : v
    );

    // atomically write back to Firestore
    await updateDoc(doc(db, "tasks", taskId), {
      versions: newVersions
    });

    // immediately mirror into local state so only this item disappears
    setTasks(prev =>
      prev.map(t =>
        t.id === taskId
          ? { ...t, versions: newVersions }
          : t
      )
    );
  }



  const handleSaveDetails = async () => {
    try {
      if (!currentUser || typeof currentUser !== 'object') {
        console.error("Invalid currentUser: not an object");
        return;
      }
      const userId = currentUser.uid || currentUser.id;
      if (!userId) {
        console.error("Invalid currentUser: missing UID or ID");
        return;
      }

      let licenseFileUrl = doctorDetails.licenseFileUrl;
      if (licenseFile) {
        const storageRef = ref(storage, `licenses/${userId}/${licenseFile.name}`);
        await uploadBytes(storageRef, licenseFile);
        licenseFileUrl = await getDownloadURL(storageRef);
      }

      const userDocRef = doc(db, "users", String(userId));
      await setDoc(userDocRef, {
        PhoneNumber: doctorDetails.PhoneNumber,
        idNumber: doctorDetails.idNumber,
        licenseNumber: doctorDetails.licenseNumber,
        specialization: doctorDetails.specialization,
        otherSpecialization: doctorDetails.otherSpecialization,
        licenseFileUrl
      }, { merge: true });

      alert('Personal details saved successfully.');
      setShowDoctorEditModal(false);
    } catch (err) {
      console.error("Error saving doctor details:", err);
      alert("An error occurred while saving. Please try again.");
    }
  };

  // flatten your nested selectedFilesByEye into a real array


  // ------------------ Render JSX ------------------
  return (
    <div className="bg-gray-50 min-h-screen">

      {isProcessing && (
        <div className="processing-overlay">
          <Spinner animation="border" variant="primary" />
          <div>Saving changes… please wait</div>
        </div>
      )}
      {isLoading && (
        <div className="loading-overlay">
          <Spinner animation="border" variant="primary" />
        </div>
      )}
      <Container className="py-4">
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
          {feedback.message && (
            <Alert
              variant={feedback.type}
              onClose={() => setFeedback({ type: '', message: '' })}
              dismissible
            >
              {feedback.message}
            </Alert>
          )}
        </div>
        {/* Header with margin-top */}
        <header className="bg-white p-4 shadow-md mb-6 mt-4">
          <div className="flex items-center justify-between w-full flex-row-reverse custom-header-container">
            {/* Hamburger Button on the right */}
            <Button
              onClick={toggleSideMenu}
              variant="link"
              className="custom-hamburger"
              aria-label="Open Menu"
            >
              <Menu className="w-8 h-8" />
            </Button>
            <span className="custom-header-title">{t("header_title")}</span>
          </div>


          {/* Side Menu */}
          {showSideMenu && (
            <nav {...swipeHandlers} className="side-menu fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50">
              <div className="p-4 flex flex-col gap-6">
                <div
                  className={`language-switch flex items-center ${i18n.language === "he" ? "flex-row-reverse" : ""}`}
                >
                  <span className="mr-2">EN</span>
                  <Switch
                    onChange={handleLanguageAndClose}
                    checked={i18n.language === "he"}
                    offColor="#ccc"
                    onColor="#ccc"
                    uncheckedIcon={false}
                    checkedIcon={false}
                    className="custom-switch"
                  />
                  <span className="ml-2">HE</span>
                </div>
                <div>
                  <Button
                    className="btn-logoutcustom"
                    onClick={handleLogout}
                    disabled={isProcessing}
                    aria-label="Logout"
                  >
                    {t('logout')}
                  </Button>
                </div>
                <div>
                </div>
                {/* Additional menu items here */}
              </div>

            </nav>
          )}
        </header>

        <main className="mt-6">
          {/*login page */}
          {!loggedIn ? (
            <Card className="shadow-lg mb-6">
              <Card.Header className="custom-card-header">{t('Login')}</Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group controlId="loginEmail" className="mb-3">
                    <Form.Label>{t('email')}</Form.Label>
                    <div className="custom-input-container">
                      <Form.Control
                        type="email"
                        placeholder={t('Enter email')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="custom-input"
                        aria-label="Login Email"
                      />
                    </div>
                  </Form.Group>
                  <Form.Group controlId="loginPassword" className="mb-3">
                    <Form.Label>{t('password')}</Form.Label>
                    <div className="custom-input-container">
                      <Form.Control
                        type="password"
                        placeholder={t('Enter password')}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="custom-input"
                        aria-label="Login Password"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleLogin(); // Trigger login when Enter is pressed
                          }
                        }}
                      />
                    </div>
                  </Form.Group>
                  <Button
                    onClick={handleLogin}
                    disabled={isProcessing}
                    variant="primary"
                    className="btn-primary"
                    aria-label="Login Button"
                  >
                    {t('login')}
                  </Button>
                  <div className="d-flex justify-content-center mt-3">
                    <Button
                      variant="outline-primary"
                      className="custom-btn"
                      onClick={handleForgotPassword}
                      disabled={isProcessing}
                    >
                      {t('ForgotPassword')}
                    </Button>
                  </div>

                  <div className="view7 no-rtl mt-4"> {/* Add margin-top here */}
                    <div className="row-view2">
                      <div className="relative-container">
                        <img
                          src="https://firebasestorage.googleapis.com/v0/b/optimizesignportal.firebasestorage.app/o/Group11.png?alt=media"
                          className="image3"
                          alt="Man"
                        />

                      </div>
                      <img
                        src="https://firebasestorage.googleapis.com/v0/b/optimizesignportal.firebasestorage.app/o/Group9.png?alt=media"
                        className="image4"
                        alt="Decorative"
                      />
                    </div>
                  </div>
                </Form>
              </Card.Body>
            </Card>

          ) : (
            <Tab.Container
              defaultActiveKey={
                userRole === "doctor"
                  ? "doctor"
                  : userRole === "admin"
                    ? "admin"
                    : "customer"
              }
            >
              <Row className="justify-content-center">
                <Col md={10} lg={8}>
                  <Nav className="custom-nav-pills position-relative px-0">
                    {/* 1) Bell absolutely positioned at far left, vertically centered */}
                    {['doctor', 'customer'].includes(currentUser?.role) && (
                      <Nav.Item
                        className="position-absolute start-0 top-50"
                        onClick={openNotificationsModal}
                        style={{ transform: 'translateY(-50%)', cursor: 'pointer', paddingLeft: '1rem' }}
                      >
                        🔔
                        {(
                          currentUser?.role === 'doctor'
                            ? newTaskCount
                            : (newSignedCount + newReturnedCount)
                        ) > 0 && (
                            <Badge bg="danger" pill className="position-absolute top-0 start-100 translate-middle">
                              {currentUser?.role === 'doctor' ? newTaskCount : (newSignedCount + newReturnedCount)}
                            </Badge>
                          )}
                      </Nav.Item>
                    )}
                    {/* 2) Push everything else rightwards so they don’t overlap the bell */}
                    <div style={{ marginLeft: '3rem', display: 'flex', gap: '0.5rem' }}>
                      {userRole === "customer" && (
                        <Nav.Item>
                          <Nav.Link eventKey="customer">{t('CustomerPortal')}</Nav.Link>
                        </Nav.Item>
                      )}
                      {userRole === "doctor" && (
                        <Nav.Item>
                          <Nav.Link eventKey="doctor">{t('DoctorPortal')}</Nav.Link>
                        </Nav.Item>
                      )}
                      <Nav.Item>
                        <Nav.Link eventKey="profile">{t('AccountDetails')}</Nav.Link>
                      </Nav.Item>
                      {userRole === "admin" && (
                        <>
                          <Nav.Item>
                            <Nav.Link eventKey="admin">{t('AdminPortal')}</Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="analytics">{t('AnalyticsDashboard')}</Nav.Link>
                          </Nav.Item>
                          <Button
                            variant="link"
                            className="p-0"
                            style={{ color: "#FFC107" }}
                            onClick={() => setShowReqFieldsModal(true)}
                            aria-label={t("RequiredFieldsSettings")}
                          >
                            <FiSettings size={24} />
                          </Button>

                        </>
                      )}
                      {userRole === "doctor" && (
                        <Button
                          variant={currentUser.isAvailable ? "success" : "danger"}
                          onClick={async () => {
                            const newStatus = !currentUser.isAvailable;
                            await updateDoc(doc(db, "users", currentUser.id), {
                              isAvailable: newStatus,
                              lastStatusUpdate: new Date().toISOString()
                            });
                            setCurrentUser(prev => ({ ...prev, isAvailable: newStatus }));
                            setFeedback({
                              type: "success",
                              message: `Status changed to ${newStatus ? "Available" : "Unavailable"}`
                            });
                          }}
                          className="d-flex align-items-center gap-2"
                          style={{
                            fontWeight: "bold",
                            padding: "5px 12px",
                            borderRadius: "20px",
                            fontSize: "0.9rem"
                          }}
                        >
                          {currentUser.isAvailable ? "🟢 Available" : "🔴 Unavailable"}
                        </Button>
                      )}
                    </div>
                  </Nav>



                  <Tab.Content>
                    {/* Doctor Portal */}
                    <Tab.Pane eventKey="doctor">
                      {/* Task Report Summary */}
                      <Card className="shadow-lg mb-4 border-primary">
                        <Card.Header className="custom-card-header-black-yellow">
                          {t('AvailableTasks')}
                        </Card.Header>

                        <Card.Body>
                          {sortedTasks.filter(task => getCurrentVersion(task).status === "Not Accepted").length === 0 ? (
                            <p className="text-center mb-0">  {t('Noavailabletasks')}</p>
                          ) : (
                            <ul className="list-unstyled mb-0">
                              {sortedTasks.map(task =>

                                getCurrentVersion(task).status === "Not Accepted" && (
                                  <li
                                    key={task.id}
                                    className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center p-3 mb-3 border rounded shadow-sm bg-white"
                                  >
                                    <div>
                                      <p className="mb-1 fw-bold">{t('StoreName')} {task.storeName}</p>
                                      <p className="mb-0 text-muted">{t('CustomTaskName')} {task.name}</p>
                                      <p className="mb-0 text-muted" style={{ fontSize: "0.9rem" }}>
                                        {t('Uploaded')} {new Date(getCurrentVersion(task).updatedAt).toLocaleDateString()}
                                      </p>
                                      <p className="mb-0 text-muted" style={{ fontSize: "0.9rem" }}>
                                        {t('age')} {task.age}
                                      </p>
                                      <p className="mb-0 text-muted" style={{ fontSize: "0.9rem" }}>
                                        {t('diseases')} {task.diseases || "None"}
                                      </p>
                                      <p className="mb-0 text-muted" style={{ fontSize: "0.9rem" }}>
                                        {t('Files')} {getCurrentVersion(task).files.length} file(s)
                                      </p>
                                    </div>
                                    <div className="mt-3 mt-md-0">
                                      <Button
                                        className="btn-succcustom"
                                        onClick={() => handleAcceptTask(task.id)}
                                        disabled={isProcessing}
                                        aria-label={`Accept Task ${task.name}`}
                                      >
                                        <ClipboardCheck className="me-1" style={{ width: "1rem", height: "1rem" }} /> {t('AcceptTask')}
                                      </Button>
                                    </div>
                                  </li>
                                )

                              )}
                            </ul>
                          )}
                        </Card.Body>

                      </Card>
                      {/* Accepted Tasks */}
                      <Card className="shadow-lg mb-4 border-warning">
                        <Card.Header className="bg-warning text-white text-center">
                          {t('accept_task')}
                        </Card.Header>
                        <Card.Body>
                          {sortedTasks.filter(task => getCurrentVersion(task).status === "Accepted by Doctor").length === 0 ? (
                            <p className="text-center mb-0">{t('Noacceptedtasks')}</p>
                          ) : (
                            <ul className="list-unstyled mb-0">
                              {sortedTasks.map(task => {
                                const taskVersion = getCurrentVersion(task);
                                if (taskVersion.status !== "Accepted by Doctor") return null;

                                return (
                                  <li
                                    key={task.id}
                                    className={`p-3 mb-3 border rounded shadow-sm ${pendingTasks[task.id] ? 'bg-light faded-task' : 'bg-white'}`}
                                  >
                                    <div className="mb-2">
                                      <p className="fw-bold mb-1">{task.name}</p>
                                      <p className="mb-0">
                                        <strong>{t('StoreName')}</strong> {task.storeName || "N/A"}
                                      </p>
                                      <p className="mb-0">
                                        <strong>{t('age')}</strong> {task.age || "N/A"}
                                      </p>
                                    </div>
                                    <div className="d-flex gap-2">
                                      <Button
                                        variant="outline-primary"
                                        className="custom-btn"
                                        onClick={() => handleOpenFileBrowser(taskVersion)}
                                      >
                                        {t('Viewfile')}
                                      </Button>
                                      <Button
                                        variant="outline-primary"
                                        className="custom-btn"
                                        onClick={() => openStoreInfo(task)}
                                      >
                                        {t('StoreInfo')}
                                      </Button>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="custom-btn"
                                        onClick={() => openHistory(task)}
                                      >
                                        {t('TaskHistory')}
                                      </Button>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="custom-btn"
                                        onClick={() => openTaskDetail(task)}
                                      >
                                        {t('view_details')}
                                      </Button>
                                    </div>
                                    <div className="d-flex flex-column gap-3 mb-2">
                                      {taskVersion.files.map(file => (
                                        <div key={file.id} className="d-flex flex-column">
                                          {/* File name on a separate line */}
                                          <div className="w-100 mb-1">
                                            <strong>{file.fileName}</strong>
                                          </div>
                                          <div className="d-flex flex-wrap gap-2">
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              className="custom-btn"
                                              onClick={() => handleDownloadFile(file.fileUrl, file.fileName)}
                                            >
                                              {t('Downloadfile')}
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    <DoctorNotesEditor
                                      notes={unsavedNotes[task.id] !== undefined ? unsavedNotes[task.id] : taskVersion.doctorNotes}
                                      onChangeNotes={(val) => {
                                        setUnsavedNotes(prev => ({ ...prev, [task.id]: val }));
                                        debouncedSaveDoctorNotes(task.id, val);
                                      }}
                                      isLocked={taskVersion.status === "Finished"}
                                    />


                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                      <Button
                                        className="btn-primarysmall"
                                        onClick={() => {
                                          const currentNote = unsavedNotes[task.id] ?? getCurrentVersion(task).doctorNotes ?? "";
                                          setUnsavedNotes(prev => ({ ...prev, [task.id]: currentNote }));
                                          setCurrentEditingTaskId(task.id);
                                          setShowExpandedEditor(true);
                                        }}
                                      >
                                        {t('EditNote')}
                                      </Button>

                                      <Button
                                        className="btn-primarysmall"
                                        onClick={() => handleDelayedSignClick(task.id)}
                                        style={{ opacity: pendingTasks[task.id] ? 0.5 : 1 }}
                                      >
                                        <PenTool className="me-1" style={{ width: "1rem", height: "1rem" }} />
                                        {pendingTasks[task.id] ? t('cancel_sign') : t('sign_complete')}
                                      </Button>




                                      <Button
                                        className="btn-dangercustom"
                                        onClick={() => handleReturnTask(task.id)}
                                        disabled={!!pendingTasks[task.id]}
                                      >
                                        <XCircle className="me-1" />
                                        {t('return_task')}
                                      </Button>
                                      {taskVersion.status === "Finished" && (
                                        <Button variant="info" onClick={() => viewSignedNotes(task)}>
                                          {t('ViewSignedNotes')}
                                        </Button>
                                      )}
                                      {pendingTasks[task.id] && (
                                        <div className="text-muted small mb-2">
                                          ⏳ {Math.floor(pendingTasks[task.id].remaining / 60)
                                            .toString()
                                            .padStart(2, '0')}:
                                          {(pendingTasks[task.id].remaining % 60).toString().padStart(2, '0')} {t('remaining')}
                                          {!pendingTasks[task.id].cancelTimer && ` (${t('locked')})`}
                                        </div>
                                      )}
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </Card.Body>
                      </Card>
                      <Card.Body>
                        <div className="d-flex flex-column gap-2 align-items-start mb-3">
                          <h5 className="fw-bold text-center">{t('taskreport')}</h5>
                          <Button
                            variant="outline-primary"
                            onClick={() => setShowReportModalDoc(true)}
                            aria-label="Generate Task Report"
                          >
                            {t('GeneratePDFReport')}
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => openReturnedTasks(true)}
                          >
                            {t('ReturnedTasks')}
                          </Button>
                        </div>
                        {currentUser && currentUser.role === "doctor" && (
                          <div className="mb-3 p-3 border rounded bg-white shadow-sm">
                            {(() => {
                              // only the tasks this doctor can actually see
                              const doctorTasks = tasks.filter(task => {
                                const cv = getCurrentVersion(task);

                                // 1) Not-accepted tasks:
                                if (cv.status === "Not Accepted") {
                                  return cv.acceptedBy === currentUser.id;
                                }

                                // 2) Accepted or finished → only by the doctor who handled them
                                return cv.acceptedBy === currentUser.id;
                              });

                              const totalTasks = doctorTasks.length;

                              const notAccepted = currentUser.isAvailable
                                ? tasks.filter(task => {
                                  const cv = getCurrentVersion(task);
                                  if (cv.status !== "Not Accepted") return false;
                                  const uploader = users.find(u => u.id === task.uploadedBy);
                                  if (!uploader || uploader.role !== "customer") return false;
                                  const mainDoctor = uploader.mainDoctor;
                                  const secondaryDoctor = uploader.secondaryDoctor;
                                  const isMain = currentUser.id === mainDoctor;
                                  const isSecondary = currentUser.id === secondaryDoctor;
                                  return (
                                    (isMain && users.find(u => u.id === mainDoctor)?.isAvailable) ||
                                    (isSecondary && !users.find(u => u.id === mainDoctor)?.isAvailable)
                                  );
                                }).length
                                : 0;

                              const accepted = doctorTasks.filter(
                                t => getCurrentVersion(t).status === "Accepted by Doctor"
                              ).length;

                              // NEW: count finished tasks
                              const finished = doctorTasks.filter(
                                t => getCurrentVersion(t).status === "Finished"
                              ).length;

                              return (
                                <>
                                  <p className="mb-1">
                                    <strong>{t('TotalTasks')}</strong> {totalTasks}
                                  </p>
                                  <p className="mb-1">
                                    <strong>{t('NotAccepted')}</strong> {notAccepted}
                                  </p>
                                  <p className="mb-1">
                                    <strong>{t('AcceptedbyDoctor')}</strong> {accepted}
                                  </p>
                                  <p className="mb-1">
                                    <strong>{t('FinishedTasks')}</strong> {finished}
                                  </p>
                                </>
                              );
                            })()}
                          </div>
                        )}

                      </Card.Body>
                      {/* Your Task History */}
                      {/* Sort Dropdown */}
                      <DropdownButton
                        id="task-sort-dropdown"
                        variant="secondary"
                        title={t(sortOrder)}
                        onSelect={(value) => setSortOrder(value)}
                        className="mb-4"
                        aria-label="Sort Tasks Dropdown"
                      >
                        <Dropdown.Item eventKey="NewestFirst">{t('NewestFirst')}</Dropdown.Item>
                        <Dropdown.Item eventKey="OldestFirst">{t('OldestFirst')}</Dropdown.Item>
                        {/* Add more sort options here, ensuring the eventKey matches your translation key */}
                      </DropdownButton>


                      {/* Existing search and filter controls */}
                      <InputGroup className="mb-3">
                        <Form.Control
                          placeholder="Search by Task or File Name"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          aria-label="Search Tasks"
                        />
                        <Button variant="outline-secondary" aria-label="Search Button">
                          <Search />
                        </Button>
                      </InputGroup>

                      {/* Task History Card */}
                      <Card className="shadow-lg mb-4">
                        <Card.Header
                          className="bg-secondary text-white text-center"
                          onClick={() => setShowTaskHistory(!showTaskHistory)}
                          style={{ cursor: "pointer" }}
                        >
                          {t('YourTaskHistory')} {showTaskHistory ? '▲' : '▼'}
                        </Card.Header>
                        <Collapse in={showTaskHistory}>
                          <div>
                            <Card.Body>
                              {sortedTasks.filter(task =>
                                getCurrentVersion(task).acceptedBy === currentUser.id &&
                                (getCurrentVersion(task).status === "Returned" || getCurrentVersion(task).status === "Finished")
                              ).length === 0 ? (
                                <p className="text-center mb-0">{t('Youhavenotasks')}</p>
                              ) : (
                                <ul className="list-unstyled mb-0">
                                  {sortedTasks
                                    .filter(task =>
                                      getCurrentVersion(task).acceptedBy === currentUser.id &&
                                      (getCurrentVersion(task).status === "Returned" || getCurrentVersion(task).status === "Finished")
                                    )
                                    .map(task => (
                                      <li
                                        key={task.id}
                                        className="d-flex justify-content-between align-items-center p-3 mb-3 border rounded shadow-sm bg-white"
                                      >
                                        <div>
                                          <p className="fw-bold mb-1">{task.name}</p>
                                          <p className="mb-0 text-muted" style={{ fontSize: "0.9rem" }}>
                                            {t('Status')} {getCurrentVersion(task).status}
                                          </p>
                                        </div>
                                        {/* Buttons Wrapper */}
                                        <div className="d-flex gap-2">
                                          <Button
                                            variant="outline-secondary"
                                            onClick={() => openHistory(task)}
                                            aria-label={`View History for Task ${task.name}`}
                                            size="sm"
                                            className="px-3 py-1 rounded"
                                          >
                                            {t('History')}
                                          </Button>
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => openTaskDetail(task)}
                                            className="px-3 py-1 rounded"
                                          >
                                            {t('view_details')}
                                          </Button>
                                        </div>
                                      </li>
                                    ))}
                                </ul>
                              )}
                            </Card.Body>
                          </div>
                        </Collapse>
                      </Card>

                    </Tab.Pane>
                    {/* Profile Tab - Account Details */}
                    <Tab.Pane eventKey="profile">
                      <Card className="custom-account-card shadow-lg mb-6">
                        <Card.Header className="custom-account-card-header">
                          {t('AccountDetails')}
                        </Card.Header>
                        <Card.Body className="custom-account-card-body">
                          {currentUser && (
                            <>
                              <p><strong>{t('Name')}</strong> {currentUser.username}</p>
                              <p><strong>{t('LastName')}</strong> {currentUser.lastName}</p>
                              <p><strong>{t('IDNumber')}</strong> {currentUser.idNumber}</p>
                              <p><strong>{t('PhoneNumber')}</strong> {currentUser.PhoneNumber}</p>
                              <p><strong>{t('Role')}</strong> {currentUser.role}</p>
                              {currentUser.role === "doctor" && (
                                <>
                                  <p><strong>{t('LicenseNumber')}</strong> {currentUser.licenseNumber}</p>
                                  <p><strong>{t('DoctorType')}</strong> {currentUser.doctorType}</p>
                                  <p><strong>{t('specialization')}</strong> {currentUser.specialization}</p>
                                </>

                              )}
                              {currentUser.signature && currentUser.role === "doctor" && (
                                <div className="mb-3">
                                  <img
                                    src={currentUser.signature}
                                    alt="Signature"
                                    style={{ maxWidth: '220px', height: '70px' }}
                                  />
                                </div>
                              )}

                              {currentUser.role === "customer" && (
                                <>
                                  <p><strong>{t('StoreName')}</strong> {currentUser.storeName}</p>
                                  <div className="mb-3">
                                    <Form.Label> {t('UploadStoreLogo')} </Form.Label>
                                    <Form.Control
                                      type="file"
                                      accept="image/*"
                                      onChange={handleLogoFileChange}
                                    />
                                    {/* If we have a currentUser.logoUrl, show a small preview */}
                                    {currentUser.logoUrl && (
                                      <div className="mt-2">
                                        <img
                                          src={currentUser.logoUrl}
                                          alt="Store Logo Preview"
                                          style={{ maxWidth: "150px", maxHeight: "150px" }}
                                        />
                                      </div>
                                    )}
                                  </div>

                                </>
                              )}
                              {currentUser.role === "doctor" && (
                                <OverlayTrigger
                                  placement="right"
                                  overlay={<Tooltip id="tooltip-update-signature">{t('Clicktoupdateyoursignature')}</Tooltip>}
                                >
                                  <Button
                                    variant="primary"
                                    onClick={() => handleOpenUserSignatureModal(currentUser.id)}
                                    className="w-100 mb-3"
                                    aria-label="Update Signature"
                                  >
                                    {t('update_signature')}
                                  </Button>
                                </OverlayTrigger>
                              )}


                              {currentUser?.role === 'doctor' && (
                                <Button
                                  variant="primary"
                                  className="w-100 mb-3"
                                  onClick={() => {
                                    // 1) Prevent if admin turned off doctor‐editing
                                    if (!allowDoctorEdit) {
                                      setFeedback({ type: 'danger', message: t('EditDisabledByAdmin') });
                                      return;
                                    }
                                    // 2) Guard against missing UID
                                    const userId = currentUser.uid || currentUser.id;
                                    if (!userId) {
                                      console.error("Invalid currentUser: missing UID or ID");
                                      return;
                                    }
                                    // 3) Populate & show doctor modal
                                    setDoctorDetails({
                                      PhoneNumber: currentUser.PhoneNumber || '',
                                      idNumber: currentUser.idNumber || '',
                                      licenseNumber: currentUser.licenseNumber || '',
                                      specialization: currentUser.specialization || '',
                                      otherSpecialization: currentUser.otherSpecialization || '',
                                      licenseFileUrl: currentUser.licenseFileUrl || ''
                                    });
                                    setShowDoctorEditModal(true);
                                  }}
                                >
                                  {t('UpdatePersonalDetails')}
                                </Button>
                              )}

                              {currentUser?.role === 'customer' && (
                                <Button
                                  variant="secondary"
                                  className="w-100 mb-3"
                                  onClick={async () => {
                                    if (!allowCustomerEdit) {
                                      setFeedback({ type: 'danger', message: t('EditDisabledByAdmin') });
                                      return;
                                    }
                                    const uid = currentUser.uid || currentUser.id;
                                    if (!uid) return console.error("Missing UID");

                                    try {
                                      const snap = await getDoc(doc(db, "users", uid));
                                      if (snap.exists()) {
                                        const d = snap.data();
                                        setCustomerDetails({
                                          storeName: d.storeName || '',
                                          lastName: d.lastName || '',
                                          PhoneNumber: d.PhoneNumber || '',
                                          address: d.address || '',
                                          anydeskuser: d.anydeskuser || '',
                                          anydeskpassword: d.anydeskpassword || ''
                                        });
                                      }
                                    } catch (err) {
                                      console.error("Load error:", err);
                                      setFeedback({ type: 'warning', message: t('LoadFailed') });
                                    }

                                    setShowCustomerEditModal(true);
                                  }}
                                >
                                  {t('UpdatePersonalDetails')}
                                </Button>
                              )}

                              <Button
                                variant="secondary"
                                onClick={handleLogout}
                                disabled={isProcessing}
                                className="w-100"
                                aria-label="Logout"
                              >
                                {t('logout')} <LogOut className="w-4 h-4 ms-2" />
                              </Button>
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </Tab.Pane>


                    {/* Customer Portal */}
                    <Tab.Pane eventKey="customer">
                      <Card className="shadow-sm mb-4">
                        <Card.Header className="custom-card-header">
                          Your Doctors
                        </Card.Header>
                        <Card.Body className="p-2 d-flex justify-content-between">
                          {/* Main Doctor */}
                          <div className="me-3">
                            <strong>{t('MainDoctor')}</strong>{" "}
                            {mainDoc ? (
                              <>
                                {mainDoc.isAvailable ? "Available" : "Unavailable"} {" "}
                                <span className="me-1">
                                  {mainDoc.isAvailable ? "🟢" : "🔴"}
                                </span>
                                {getUserDisplayName(mainDoc)}{" "}
                              </>
                            ) : (
                              <span className="text-muted">None</span>
                            )}
                          </div>

                          {/* Second Doctor */}
                          <div>
                            <strong> {t('SecondDoctor')}</strong>{" "}
                            {secondaryDoc ? (
                              <>
                                {getUserDisplayName(secondaryDoc)}{" "}
                                <span className="me-1">
                                  {secondaryDoc.isAvailable ? "🟢" : "🔴"}
                                </span>
                                {secondaryDoc.isAvailable ? "Available" : "Unavailable"} {" "}
                              </>
                            ) : (
                              <span className="text-muted">None</span>
                            )}
                          </div>
                        </Card.Body>
                      </Card>

                      <Card className="shadow-lg mb-6">
                        <Card.Header className="custom-card-header">{t('create_task')}</Card.Header>
                        <Card.Body>
                          <Form>
                            {/* Existing form fields remain unchanged */}
                            <Form.Group controlId="customTaskName" className="mb-3">
                              <Form.Label>{t('CustomTaskName')}</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter custom task name"
                                value={customTaskName}
                                onChange={(e) => setCustomTaskName(e.target.value)}
                                aria-label="Custom Task Name"
                              />
                            </Form.Group>
                            <Form.Group controlId="patientID" className="mb-3">
                              <Form.Label>{t('patient_id')}</Form.Label>
                              <Form.Control
                                type="text"              // keep as text so leading zeros aren’t lost
                                inputMode="numeric"      // mobile keyboard shows digits
                                pattern="\d{1,9}"        // HTML validation (1-9 digits)
                                maxLength={9}            // hard cap in the DOM
                                placeholder={t('EnterPatientID')}
                                value={patientID}
                                onChange={(e) => {
                                  // strip non-digits, then clamp to 9 chars
                                  const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                                  setPatientID(digits);
                                }}
                                required
                                aria-label="Patient ID"
                              />
                            </Form.Group>

                            <Form.Group controlId="age" className="mb-3">
                              <Form.Label>{t('age')}</Form.Label>
                              <Form.Control
                                type="number"
                                min="0"           // ⬅ only 0-120 allowed
                                max="120"
                                step="1"
                                placeholder={t('EnterYourAge')}
                                value={age}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  // allow empty string while typing, but keep within 0-120
                                  if (v === '' || (+v >= 0 && +v <= 120)) setAge(v);
                                }}
                                required
                                aria-label="Age"
                              />
                            </Form.Group>
                            <Form.Group controlId="diseases" className="mb-3">
                              <Form.Label>{t('diseases')}</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter any diseases (comma-separated)"
                                value={diseases}
                                onChange={(e) => setDiseases(e.target.value)}
                                aria-label="Diseases"
                              />
                            </Form.Group>
                            <Form.Group controlId="taskNotes" className="mb-3">
                              <Form.Label>{t('additional_notes')}</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Enter any additional notes..."
                                value={taskNotes}
                                onChange={(e) => setTaskNotes(e.target.value)}
                                aria-label="Task Notes"
                              />
                            </Form.Group>

                            <Card className="mb-4">
                              <Card.Header className="custom-card-header">
                                {t("Upload OCT Scans")}
                              </Card.Header>
                              <Card.Body className="p-0">
                                <Table bordered responsive size="sm" className="align-middle text-center m-0">
                                  <colgroup>
                                    {colWidths.map((w, i) => (
                                      <col key={i} style={{ width: w }} />
                                    ))}
                                  </colgroup>
                                  <thead className="bg-light">
                                    <tr>
                                      <th className="py-2">{t("Scan Type")}</th>
                                      <th className="py-2">{t("Right Eye")}</th>
                                      <th className="py-2">{t("Left Eye")}</th>
                                      <th className="py-2">{t("Both Eyes")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {["Macula Map", "Macula Multi", "Fundus"].map((spec, row) => (
                                      <tr key={spec}>
                                        {/* Scan Type */}
                                        <td className="fw-bold py-2">{t(spec)}</td>

                                        {/* Right Eye */}
                                        <UploadCell
                                          side="right"
                                          spec={spec}
                                          selected={selectedFilesByEye.right[spec]}
                                          preview={previews.right[spec]}
                                          onSelect={f => {
                                            const url = f ? URL.createObjectURL(f) : null;
                                            setPreviews(p => ({ ...p, right: { ...p.right, [spec]: url } }));
                                            setSelectedFilesByEye(p => ({ ...p, right: { ...p.right, [spec]: f } }));
                                          }}
                                          onDelete={() => {
                                            URL.revokeObjectURL(previews.right[spec]);
                                            setPreviews(p => ({ ...p, right: { ...p.right, [spec]: null } }));
                                            setSelectedFilesByEye(p => ({ ...p, right: { ...p.right, [spec]: null } }));
                                          }}
                                        />

                                        {/* Left Eye */}
                                        <UploadCell
                                          side="left"
                                          spec={spec}
                                          selected={selectedFilesByEye.left[spec]}
                                          preview={previews.left[spec]}
                                          onSelect={f => {
                                            const url = f ? URL.createObjectURL(f) : null;
                                            setPreviews(p => ({ ...p, left: { ...p.left, [spec]: url } }));
                                            setSelectedFilesByEye(p => ({ ...p, left: { ...p.left, [spec]: f } }));
                                          }}
                                          onDelete={() => {
                                            URL.revokeObjectURL(previews.left[spec]);
                                            setPreviews(p => ({ ...p, left: { ...p.left, [spec]: null } }));
                                            setSelectedFilesByEye(p => ({ ...p, left: { ...p.left, [spec]: null } }));
                                          }}
                                        />

                                        {/* Both Eyes (span 3 rows) */}
                                        {row === 0 && (
                                          <UploadCell
                                            side="both"
                                            spec="Both"
                                            selected={selectedFilesByEye.both.Both}
                                            preview={previews.both.Both}
                                            onSelect={f => {
                                              const url = f ? URL.createObjectURL(f) : null;
                                              setPreviews(p => ({ ...p, both: { Both: url } }));
                                              setSelectedFilesByEye(p => ({ ...p, both: { Both: f } }));
                                            }}
                                            onDelete={() => {
                                              URL.revokeObjectURL(previews.both.Both);
                                              setPreviews(p => ({ ...p, both: { Both: null } }));
                                              setSelectedFilesByEye(p => ({ ...p, both: { Both: null } }));
                                            }}
                                          />
                                        )}
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </Card.Body>
                            </Card>

                            {uploading && (
                              <GlassesBackgroundBar
                                progress={uploadProgress}
                                aria-label="Uploading Files Progress Bar"
                              />
                            )}
                            <Button
                              onClick={handleFileUpload}
                              disabled={uploading}
                              variant="primary"
                              className="btn-primary"
                            >
                              {uploading ? t('Uploading…') : t('Upload Files')}
                            </Button>

                          </Form>
                        </Card.Body>
                      </Card>

                      <div className="mt-6">
                        {/* Task Count Summary */}
                        {currentUser && (
                          <div className="mb-3 p-3 border rounded bg-white shadow-sm">
                            {(() => {
                              const customerTasks = tasks.filter(task => task.uploadedBy === currentUser.id);
                              const totalTasks = customerTasks.length;
                              const notAccepted = customerTasks.filter(task => getCurrentVersion(task).status === "Not Accepted").length;
                              const accepted = customerTasks.filter(task => getCurrentVersion(task).status === "Accepted by Doctor").length;
                              const finished = customerTasks.filter(task => getCurrentVersion(task).status === "Finished").length;
                              return (
                                <>
                                  <p className="mb-1"><strong>{t('TotalTasks')}</strong> {totalTasks}</p>
                                  <p className="mb-1"><strong>{t('NotAccepted')}</strong> {notAccepted}</p>
                                  <p className="mb-1"><strong>{t('AcceptedbyDoctor')}</strong> {accepted}</p>
                                  <p className="mb-1"><strong>{t('Finished')}</strong> {finished}</p>
                                </>
                              );
                            })()}
                          </div>
                        )}
                        <Card.Body>
                          {/* Add a Report Button */}
                          <div className="d-flex flex-column gap-2 align-items-start mb-3">
                            <h5 className="fw-bold text-center">{t('taskreport')}</h5>
                            <Button
                              variant="outline-primary"
                              onClick={() => setShowReportModalcustomer(true)}
                              aria-label="Generate Task Report"
                            >
                              {t('GeneratePDFReport')}
                            </Button>
                            <Button
                              variant="outline-danger"
                              onClick={() => openReturnedTasks(false)}
                            >
                              {t('ReturnedTasks')}
                            </Button>
                          </div>
                        </Card.Body>
                        {/* Existing search and filter controls */}
                        <InputGroup className="mb-3">
                          <Form.Control
                            placeholder="Search by Task or File Name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Search Tasks"
                          />
                          <Button variant="outline-secondary" aria-label="Search Button">
                            <Search />
                          </Button>
                        </InputGroup>
                        <DropdownButton
                          id="task-sort-dropdown"
                          variant="secondary"
                          title={t(sortOrder)}
                          onSelect={(value) => setSortOrder(value)}
                          className="mb-4"
                          aria-label="Sort Tasks Dropdown"
                        >
                          <Dropdown.Item eventKey="NewestFirst">{t('NewestFirst')}</Dropdown.Item>
                          <Dropdown.Item eventKey="OldestFirst">{t('OldestFirst')}</Dropdown.Item>
                          {/* Add more sort options here, ensuring the eventKey matches your translation key */}
                        </DropdownButton>

                        <DropdownButton
                          id="task-filter-dropdown"
                          variant="secondary"
                          title={
                            selectedStatus === 'All'
                              ? t('All')
                              : t(selectedStatus)
                          }
                          onSelect={value => setSelectedStatus(value)}
                          className="mb-4"
                          aria-label="Filter Tasks Dropdown"
                        >
                          <Dropdown.Item eventKey="All">{t('All')}</Dropdown.Item>
                          <Dropdown.Item eventKey="Not Accepted">{t('NotAcceptedM')}</Dropdown.Item>
                          <Dropdown.Item eventKey="Accepted by Doctor">{t('AcceptedbyDoctorM')}</Dropdown.Item>
                          <Dropdown.Item eventKey="Returned">{t('Returned')}</Dropdown.Item>
                          <Dropdown.Item eventKey="Finished">{t('Finished')}</Dropdown.Item>
                        </DropdownButton>


                        <h2 className="text-lg font-bold">{t('YourTasks')}</h2>
                        {/* Non-finished Tasks */}
                        {sortedTasks.filter(task => getCurrentVersion(task).status !== "Finished").length === 0 ? (
                          <p>{t('Noavailabletasks')}</p>
                        ) : (
                          <ul className="mt-2 space-y-2">
                            {sortedTasks
                              .filter(task => getCurrentVersion(task).status !== "Finished")
                              .map((task) => (
                                <li key={task.id} className="p-4 border rounded-lg shadow-sm bg-white flex flex-col justify-between items-start mb-4">
                                  <div>
                                    <p>{task.name}</p>
                                    <p className="mb-0">
                                      <strong>{t('age')}</strong> {task.age || "N/A"}
                                    </p>
                                    <p style={{ color: getStatusColor(getCurrentVersion(task).status) }}>
                                      {t('Status')} {getCurrentVersion(task).status}
                                      {getCurrentVersion(task).status === "Returned" && (
                                        <Button
                                          variant="outline-primary"
                                          className="custom-btn"
                                          onClick={() => handleResendTask(task.id)}
                                          disabled={isProcessing}
                                          aria-label={`Resend Task ${task.name}`}
                                        >
                                          {t('ResendTask')}
                                        </Button>
                                      )}
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2 mt-3">
                                    <Button variant="outline-primary"
                                      className="custom-btn" onClick={() => openTaskDetail(task)} aria-label={`View Details for Task ${task.name}`}>
                                      {t('view_details')}
                                    </Button>
                                    <Button variant="outline-primary"
                                      className="custom-btn" onClick={() => openHistory(task)} aria-label={`View History for Task ${task.name}`}>
                                      {t('view_history')}
                                    </Button>
                                    {(getCurrentVersion(task).status === "Not Accepted" || getCurrentVersion(task).status === "Returned") && (
                                      <>
                                        <Button variant="warning" onClick={() => handleReupload(task.id)}
                                          disabled={isProcessing}
                                          aria-label={`Re-upload Task ${task.name}`}>
                                          {t('reupload')}
                                        </Button>
                                        <Button variant="danger" onClick={() => handleDeleteTask(task.id)}
                                          disabled={isProcessing}
                                          aria-label={`Delete Task ${task.name}`}>
                                          {t('delete')}
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </li>
                              ))}
                          </ul>
                        )}
                        {/* Finished Tasks Collapsible Card */}
                        <Card className="shadow-lg mb-4">
                          <Card.Header
                            className="bg-secondary text-white text-center"
                            onClick={() => setShowFinishedTasks(!showFinishedTasks)}
                            style={{ cursor: "pointer" }}
                          >
                            {t('FinishedTasks')} {showFinishedTasks ? '▲' : '▼'}
                          </Card.Header>
                          <Collapse in={showFinishedTasks}>
                            <div>
                              <Card.Body>
                                {sortedTasks.filter(task => getCurrentVersion(task).status === "Finished").length === 0 ? (
                                  <p className="text-center mb-0">{t('NoFinishedTasks')}</p>
                                ) : (
                                  <ul className="list-unstyled mb-0">
                                    {sortedTasks
                                      .filter(task => getCurrentVersion(task).status === "Finished")
                                      .map(task => (
                                        <li
                                          key={task.id}
                                          className="p-4 border rounded-lg shadow-sm bg-white flex flex-col justify-between items-start mb-4"
                                        >
                                          <div>
                                            <p>{task.name}</p>
                                            <p className="mb-0">
                                              <strong>{t('age')}</strong> {task.age || "N/A"}
                                            </p>
                                            <p style={{ color: getStatusColor(getCurrentVersion(task).status) }}>
                                              {t('Status')} {getCurrentVersion(task).status}
                                            </p>
                                          </div>
                                          <div className="d-flex gap-2 mt-3">
                                            <Button variant="outline-primary" onClick={() => openTaskDetail(task)} aria-label={`View Details for Task ${task.name}`}>
                                              {t('view_details')}
                                            </Button>
                                            <Button variant="outline-primary" onClick={() => openHistory(task)} aria-label={`View History for Task ${task.name}`}>
                                              {t('view_history')}
                                            </Button>
                                          </div>
                                        </li>
                                      ))}
                                  </ul>
                                )}
                              </Card.Body>
                            </div>
                          </Collapse>
                        </Card>
                      </div>
                    </Tab.Pane>

                    {/* Admin Portal */}
                    <Tab.Pane eventKey="admin">
                      <Card className="shadow-lg mb-6">
                        <Card.Header className="custom-card-header">
                          {t('CreateUser')}
                        </Card.Header>
                        <Card.Body>
                          <Form autoComplete="off">
                            {/* Prevent browser autofill */}
                            <input
                              type="text"
                              name="prevent_autofill"
                              style={{ display: 'none' }}
                              autoComplete="off"
                            />

                            {/* First Name */}
                            <Form.Group controlId="createUsername" className="mb-3">
                              <Form.Label className={requiredFields.includes('username') ? 'text-danger' : ''}>
                                {t('Name')}{requiredFields.includes('username') && ' *'}
                              </Form.Label>
                              <Form.Control
                                required
                                type="text"
                                name="username"
                                value={userData.username}
                                onChange={handleUserDataChange}
                                placeholder="First Name"
                                isInvalid={feedback?.fields?.includes('username')}
                              />
                            </Form.Group>

                            {/* Last Name */}
                            <Form.Group controlId="createLastName" className="mb-3">
                              <Form.Label className={requiredFields.includes('lastName') ? 'text-danger' : ''}>
                                {t('LastName')}{requiredFields.includes('lastName') && ' *'}
                              </Form.Label>
                              <Form.Control
                                required
                                type="text"
                                name="lastName"
                                value={userData.lastName}
                                onChange={handleUserDataChange}
                                placeholder="Last Name"
                                isInvalid={feedback?.fields?.includes('lastName')}
                              />
                            </Form.Group>

                            {/* Email */}
                            <Form.Group controlId="createEmail" className="mb-3">
                              <Form.Label className={requiredFields.includes('email') ? 'text-danger' : ''}>
                                {t('email')}{requiredFields.includes('email') && ' *'}
                              </Form.Label>
                              <Form.Control
                                required
                                type="email"
                                name="email"
                                value={userData.email}
                                onChange={handleUserDataChange}
                                placeholder="Email"
                                isInvalid={feedback?.fields?.includes('email')}
                              />
                            </Form.Group>

                            {/* Password */}
                            <Form.Group controlId="createPassword" className="mb-3">
                              <Form.Label className={requiredFields.includes('password') ? 'text-danger' : ''}>
                                {t('password')}{requiredFields.includes('password') && ' *'}
                              </Form.Label>
                              <Form.Control
                                required
                                type="password"
                                name="password"
                                value={userData.password}
                                onChange={handleUserDataChange}
                                placeholder="Password"
                                autoComplete="new-password"
                                isInvalid={feedback?.fields?.includes('password')}
                              />
                            </Form.Group>

                            {/* Phone Number */}
                            <Form.Group controlId="createPhoneNumber" className="mb-3">
                              <Form.Label className={requiredFields.includes('PhoneNumber') ? 'text-danger' : ''}>
                                {t('PhoneNumber')}{requiredFields.includes('PhoneNumber') && ' *'}
                              </Form.Label>
                              <Form.Control
                                required
                                type="text"
                                name="PhoneNumber"
                                value={userData.PhoneNumber}
                                onChange={handleUserDataChange}
                                placeholder="Phone Number"
                                isInvalid={feedback?.fields?.includes('PhoneNumber')}
                              />
                            </Form.Group>


                            {/* Customer-only */}
                            {userData.role === 'customer' && (
                              <>
                                {userData.role === 'customer' && (
                                  <Form.Group controlId="createStoreName" className="mb-3">
                                    <Form.Label className="text-danger">
                                      {t('StoreName')} *
                                    </Form.Label>
                                    <Form.Control
                                      required
                                      type="text"
                                      name="storeName"
                                      value={userData.storeName}
                                      onChange={handleUserDataChange}
                                      placeholder="Store Name"
                                      aria-required="true"
                                      isInvalid={feedback?.fields?.includes('storeName')}
                                    />
                                  </Form.Group>
                                )}

                                {userData.role === 'customer' && (
                                  <>
                                    <Form.Group controlId="createMainDoctor" className="mb-3">
                                      <Form.Label className="text-danger">
                                        {t('MainDoctor')} *
                                      </Form.Label>
                                      <Form.Select
                                        required
                                        name="mainDoctor"
                                        value={userData.mainDoctor}
                                        onChange={handleUserDataChange}
                                        aria-label="Main Doctor"
                                        isInvalid={feedback?.fields?.includes('mainDoctor')}
                                      >
                                        <option value="">{t('SelectDoctor')}</option>
                                        {users
                                          .filter(u => u.role === 'doctor')
                                          .map(doc => (
                                            <option key={doc.id} value={doc.id}>
                                              {doc.username} {doc.lastName}
                                            </option>
                                          ))}
                                      </Form.Select>
                                    </Form.Group>

                                    <Form.Group controlId="createSecondaryDoctor" className="mb-3">
                                      <Form.Label className="text-danger">
                                        {t('SecondaryDoctor')} *
                                      </Form.Label>
                                      <Form.Select
                                        required
                                        name="secondaryDoctor"
                                        value={userData.secondaryDoctor}
                                        onChange={handleUserDataChange}
                                        aria-label="Secondary Doctor"
                                        isInvalid={feedback?.fields?.includes('secondaryDoctor')}
                                      >
                                        <option value="">{t('SelectDoctor')}</option>
                                        {users
                                          .filter(u => u.role === 'doctor')
                                          .map(doc => (
                                            <option key={doc.id} value={doc.id}>
                                              {doc.username} {doc.lastName}
                                            </option>
                                          ))}
                                      </Form.Select>
                                    </Form.Group>
                                  </>
                                )}

                                <Form.Group controlId="createAnydeskUser" className="mb-3">
                                  <Form.Label>{t('anydeskuser')}</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="anydeskuser"
                                    value={userData.anydeskuser}
                                    onChange={handleUserDataChange}
                                    placeholder="Anydesk User"
                                    aria-label="Anydesk User"
                                  />
                                </Form.Group>

                                <Form.Group controlId="createAnydeskPassword" className="mb-3">
                                  <Form.Label>{t('anydeskpassword')}</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="anydeskpassword"
                                    value={userData.anydeskpassword}
                                    onChange={handleUserDataChange}
                                    placeholder="Anydesk Password"
                                    aria-label="Anydesk Password"
                                  />
                                </Form.Group>
                              </>
                            )}
                            {/* ID Number */}
                            <Form.Group controlId="createIDNumber" className="mb-3">
                              <Form.Label>{t('IDNumber')}</Form.Label>
                              <Form.Control
                                type="text"
                                name="idNumber"
                                value={userData.idNumber}
                                onChange={handleUserDataChange}
                                placeholder="ID Number"
                                aria-label="ID Number"
                              />
                            </Form.Group>



                            {/* Address */}
                            <Form.Group controlId="createAddress" className="mb-3">
                              <Form.Label>{t('Address')}</Form.Label>
                              <Form.Control
                                type="text"
                                name="address"
                                value={userData.address}
                                onChange={handleUserDataChange}
                                placeholder="Address"
                                aria-label="Address"
                              />
                            </Form.Group>


                            {/* Doctor-only */}
                            {userData.role === 'doctor' && (
                              <>
                                {/* License Number */}
                                <Form.Group controlId="createLicenseNumber" className="mb-3">
                                  <Form.Label>{t('LicenseNumber')}</Form.Label>
                                  <Form.Control
                                    type="text"
                                    name="licenseNumber"
                                    value={userData.licenseNumber}
                                    onChange={handleUserDataChange}
                                    placeholder="License Number"
                                    aria-label="License Number"
                                  />
                                </Form.Group>
                                <Form.Group controlId="createDoctorType" className="mb-3">
                                  <Form.Label>{t('DoctorType')}</Form.Label>
                                  <Form.Select
                                    name="doctorType"
                                    value={userData.doctorType}
                                    onChange={handleUserDataChange}
                                  >
                                    <option value="Professor">{t('Professor')}</option>
                                    <option value="Doctor">{t('Doctor')}</option>
                                  </Form.Select>
                                </Form.Group>

                                <Form.Group controlId="createSpecialization" className="mb-3">
                                  <Form.Label>{t('specialization')}</Form.Label>
                                  <Form.Select
                                    name="specialization"
                                    value={userData.specialization}
                                    onChange={handleUserDataChange}
                                  >
                                    <option value="cornea">{t('cornea')}</option>
                                    <option value="retina">{t('retina')}</option>
                                    <option value="kids">{t('KIDS')}</option>
                                    <option value="other">{t('OTHER')}</option>
                                  </Form.Select>
                                </Form.Group>

                                {userData.specialization === 'other' && (
                                  <Form.Group controlId="createOtherSpecialization" className="mb-3">
                                    <Form.Label>{t('specialization')}</Form.Label>
                                    <Form.Control
                                      type="text"
                                      name="otherSpecialization"
                                      value={userData.otherSpecialization}
                                      onChange={handleUserDataChange}
                                      placeholder={t('please_specify_specialization')}
                                    />
                                  </Form.Group>
                                )}

                                <Form.Group controlId="createLicenseFile" className="mb-3">
                                  <Form.Label>{t('UploadDoctorLicens')}</Form.Label>
                                  <Form.Control
                                    type="file"
                                    accept=".pdf,image/*"
                                    onChange={e => setLicenseFile(e.target.files[0])}
                                  />
                                </Form.Group>
                              </>
                            )}

                            {/* Role Selector */}
                            <DropdownButton id="role-dropdown" title={`Role: ${userData.role}`} className="mb-3">
                              <Dropdown.Item onClick={() => setUserData({ ...userData, role: 'customer' })}>
                                {t('Customer')}
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => setUserData({ ...userData, role: 'doctor' })}>
                                {t('Doctor')}
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => setUserData({ ...userData, role: 'admin' })}>
                                {t('Admin')}
                              </Dropdown.Item>
                            </DropdownButton>

                            {/* Submit */}
                            <Button
                              variant="primary"
                              className="w-100"
                              onClick={handleCreateUser}
                              disabled={isProcessing}
                            >
                              {t('CreateUser')}
                            </Button>
                          </Form>
                        </Card.Body>
                      </Card>



                      <Card className="shadow-lg mb-6">
                        <Card.Header className="custom-card-header" >{t('ManageExistingUsers')}</Card.Header>
                        <Card.Body>
                          {users.length === 0 ? (
                            <p>No users available</p>
                          ) : (
                            <ul className="list-unstyled">
                              {users.map((user) => (
                                <li key={user.id} className="p-2 border rounded mb-2">
                                  <div>
                                    <strong>
                                      {user.username} {user.lastName}
                                    </strong>{" "}
                                    ({user.role})
                                  </div>
                                  <div>{t('email')} {user.email}</div>
                                  {user.role === "customer" && (
                                    <div>{t('StoreName')} {user.storeName}</div>
                                  )}
                                  {user.role === "doctor" && (
                                    <div>{t('DoctorType')} {user.doctorType}</div>
                                  )}
                                  <div className="d-flex gap-2 mt-2">
                                    <Button
                                      variant="warning"
                                      onClick={() => openEditUserModal(user.id)}
                                      aria-label={`Edit User ${user.username} ${user.lastName}`}
                                    >
                                      {t('EditUser')}
                                    </Button>

                                    <Button
                                      variant="danger"
                                      onClick={() => handleDeleteUser(user.id)}
                                      aria-label={`Delete User ${user.username}`}
                                    >
                                      {t('delete')}
                                    </Button>

                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </Card.Body>

                      </Card>
                    </Tab.Pane>
                    <Tab.Pane eventKey="analytics">
                      <AdminAnalyticsDashboard tasks={tasks} users={users} />
                    </Tab.Pane>
                  </Tab.Content>
                </Col>
              </Row>
            </Tab.Container>
          )}
        </main>

      </Container>
      {/* Report Modal for Customer */}
      <Modal show={showReportModalcustomer} onHide={() => setShowReportModalcustomer(false)}
        dialogClassName="custom-history-modal"
        centered>
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('generate_report')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="reportStartDate" className="mb-3">
              <Form.Label>{t('StartDate')}</Form.Label>
              <Form.Control
                type="date"
                value={reportStartDatecustomer}
                onChange={(e) => setReportStartDatecustomer(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="reportEndDate" className="mb-3">
              <Form.Label>{t('EndDate')}</Form.Label>
              <Form.Control
                type="date"
                value={reportEndDatecustomer}
                onChange={(e) => setReportEndDatecustomer(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="reportStatus" className="mb-3">
              <Form.Label>{t('Status')}</Form.Label>
              <Form.Select
                value={reportStatus}
                onChange={(e) => setReportStatus(e.target.value)}
              >
                <option value="All">{t('All')}</option>
                <option value="Not Accepted">{t('NotAcceptedM')}</option>
                <option value="Accepted by Doctor">{t('AcceptedbyDoctorM')}</option>
                <option value="Returned">{t('Returned')}</option>
                <option value="Finished">{t('FinishedM')}</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="doctorSelect" className="mb-3">
              <Form.Label>{t('SelectDoctor')}</Form.Label>
              <Form.Select
                value={selectedDoctor}
                onChange={(e) => setSelectedDoctor(e.target.value)}
              >
                <option value="">{t('AllDoctors')}</option>
                {users
                  .filter((u) => u.role === "doctor") // Assuming doctors have a "role" field
                  .map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.username} {doctor.lastName}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModalcustomer(false)}>
            {t('cancel')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              generateTaskReportPDFCustomer(reportStartDatecustomer, reportEndDatecustomer, reportStatus, selectedDoctor);
              setShowReportModalcustomer(false);
            }}
          >
            {t('generate_report')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Report Modal for Doctor */}
      <Modal show={showReportModalDoc}
        size="lg" centered

        backdrop="static"
        keyboard={false}
        onHide={() => setShowReportModalDoc(false)}
        dialogClassName="custom-history-modal">

        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('taskreport')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="reportStartDateDoc" className="mb-3">
              <Form.Label>{t('StartDate')}</Form.Label>
              <Form.Control
                type="date"
                value={reportStartDateDoc}
                onChange={(e) => setReportStartDateDoc(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="reportEndDateDoc" className="mb-3">
              <Form.Label>{t('EndDate')}</Form.Label>
              <Form.Control
                type="date"
                value={reportEndDateDoc}
                onChange={(e) => setReportEndDateDoc(e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="customerSelectDoc" className="mb-3">
              <Form.Label>{t('SelectCustomer')}</Form.Label>
              <Form.Select
                value={selectedCustomerDoc}
                onChange={(e) => setSelectedCustomerDoc(e.target.value)}
              >
                <option value="">{t('AllCustomers')}</option>
                {users
                  .filter((u) => u.role === "customer")
                  .map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.username} {customer.lastName}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModalDoc(false)}>
            {t('cancel')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              generateTaskReportPDFDoc(selectedCustomerDoc, reportStartDateDoc, reportEndDateDoc);
              setShowReportModalDoc(false); // Fixed the variable name typo here
            }}
          >
            {t('generate_report')}
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Signature Editing Modal (for adding/updating user signatures) */}
      <Modal
        show={showSignatureModal}
        onHide={() => {
          setShowSignatureModal(false);
          setSignatureFile(null);
          setSignaturePreview(null);
        }}
        centered
      >
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('crop_signature')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: 300, position: "relative" }}>
          {!signaturePreview ? (
            <Form.Group>
              <Form.Label>{t('Select an image of your signature')}</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setSignatureFile(file);
                  const reader = new FileReader();
                  reader.onload = () => setSignaturePreview(reader.result);
                  reader.readAsDataURL(file);
                }}
              />
            </Form.Group>
          ) : (
            <>
              <div style={{ position: "relative", width: "100%", height: 250 }}>
                <Cropper
                  image={signaturePreview}
                  crop={crop}
                  zoom={zoom}
                  aspect={null}               // free‐form box
                  cropSize={{ width: 200, height: 90 }}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}

                  // ↓ tweak how fast wheel/pinch zooms
                  zoomSpeed={0.5}             // 50% of default speed
                  minZoom={1}
                  maxZoom={3}

                  style={{
                    // container style: dark bg so white box pops
                    containerStyle: {
                      width: "100%",
                      height: "300px",
                      background: "#333",
                    },
                    // crop‐area style: dashed border + rounded corners
                    cropAreaStyle: {
                      border: "2px dashed #fff",
                      borderRadius: "4px",
                    },
                  }}
                />

                {/* Add a slider so users can zoom in 0.1× increments */}
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={e => setZoom(Number(e.target.value))}
                  style={{ width: "100%", marginTop: "1rem" }}
                />



              </div>
              <Form.Range
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={e => setZoom(e.target.value)}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowSignatureModal(false);
              setSignatureFile(null);
              setSignaturePreview(null);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveUserSignature}
            disabled={!signaturePreview || isProcessing}
          >
            {t('Save')}
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Return Task Modal */}
      <Modal show={showReturnTaskModal} onHide={() => setShowReturnTaskModal(false)} size="lg"
        centered>
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>Reason for Returning Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="returnReason" className="mb-3">
              <Form.Label>Enter reason for returning the task:</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter reason..."
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                aria-label="Return Reason"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReturnTaskModal(false)} aria-label="Close Return Task Modal">
            Close
          </Button>
          <Button variant="danger"
            nClick={() => returnTask(returningTaskId)}
            onClick={() => {
              console.log("🔄 Return Task clicked", {
                taskId: returningTaskId,
                reason: returnReason,
                tasksInState: tasks.length,
              });
              returnTask(returningTaskId);
            }}
            disabled={isProcessing}
            aria-label="Return Task Button">
            Return Task
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditUserModal} onHide={() => setShowEditUserModal(false)} size="lg"
        fullscreen="sm-down">
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="editusername" className="mb-3">
              <Form.Label>{t('Name')}</Form.Label>
              <Form.Control
                type="text"
                value={editUserData.username}
                onChange={(e) =>
                  setEditUserData({
                    ...editUserData,
                    username: e.target.value,
                  })
                }
                placeholder="First Name"
                aria-label="Edit First Name"
              />
            </Form.Group>
            <Form.Group controlId="editLastName" className="mb-3">
              <Form.Label>{t('LastName')}</Form.Label>
              <Form.Control
                type="text"
                value={editUserData.lastName}
                onChange={(e) =>
                  setEditUserData({
                    ...editUserData,
                    lastName: e.target.value,
                  })
                }
                placeholder="Last Name"
                aria-label="Edit Last Name"
              />
            </Form.Group>
            <Form.Group controlId="editEmail" className="mb-3">
              <Form.Label>{t('email')}</Form.Label>
              <Form.Control
                type="email"
                value={editUserData.email}
                onChange={(e) =>
                  setEditUserData({
                    ...editUserData,
                    email: e.target.value,
                  })
                }
                placeholder="Email"
                aria-label="Edit Email"
              />
            </Form.Group>
            <Form.Group controlId="editIDNumber" className="mb-3">
              <Form.Label>{t('IDNumber')}</Form.Label>
              <Form.Control
                type="text"
                value={editUserData.idNumber}
                onChange={(e) =>
                  setEditUserData({
                    ...editUserData,
                    idNumber: e.target.value,
                  })
                }
                placeholder="ID Number"
                aria-label="Edit ID Number"
              />
            </Form.Group>
            <Form.Group controlId="editLicenseNumber" className="mb-3">
              <Form.Label>{t('LicenseNumber')}</Form.Label>
              <Form.Control
                type="text"
                value={editUserData.licenseNumber}
                onChange={(e) =>
                  setEditUserData({
                    ...editUserData,
                    licenseNumber: e.target.value,
                  })
                }
                placeholder="License Number"
                aria-label="Edit License Number"
              />
            </Form.Group>
            <Form.Group controlId="editAddress" className="mb-3">
              <Form.Label>{t('Address')}</Form.Label>
              <Form.Control
                type="text"
                value={editUserData.address}
                onChange={(e) =>
                  setEditUserData({
                    ...editUserData,
                    address: e.target.value,
                  })
                }
                placeholder="Address"
                aria-label="Edit Address"
              />
            </Form.Group>
            <Form.Group controlId="editPhoneNumber" className="mb-3">
              <Form.Label>{t('PhoneNumber')}</Form.Label>
              <Form.Control
                type="text"
                value={editUserData.PhoneNumber}
                onChange={(e) =>
                  setEditUserData({
                    ...editUserData,
                    PhoneNumber: e.target.value,
                  })
                }
                placeholder="Phone Number "
                aria-label="Edit Phone Number"
              />
            </Form.Group>
            {editUserData.role === "customer" && (
              <Form.Group controlId="editStoreName" className="mb-3">
                <Form.Label>{t('StoreName')}</Form.Label>
                <Form.Control
                  type="text"
                  value={editUserData.storeName}
                  onChange={(e) =>
                    setEditUserData({
                      ...editUserData,
                      storeName: e.target.value,
                    })
                  }
                  placeholder="Store Name"
                  aria-label="Edit Store Name"
                />
              </Form.Group>
            )}

            {editUserData.role === "customer" && (
              <>
                <Form.Group>
                  <Form.Label>Main Doctor</Form.Label>
                  <Form.Select
                    name="mainDoctor"
                    value={editUserData.mainDoctor}
                    onChange={handleEditUserDataChange}
                  >
                    <option value="">Select Doctor</option>
                    {users.filter(u => u.role === "doctor").map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.username} {doctor.lastName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group>
                  <Form.Label>Secondary Doctor</Form.Label>
                  <Form.Select
                    name="secondaryDoctor"
                    value={editUserData.secondaryDoctor}
                    onChange={handleEditUserDataChange}
                  >
                    <option value="">Select Doctor</option>
                    {users.filter(u => u.role === "doctor").map(doctor => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.username} {doctor.lastName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </>
            )}

            {editUserData.role === "customer" && (
              <Form.Group controlId="eitanydeskuser" className="mb-3">
                <Form.Label>{t('anydeskuser')}</Form.Label>
                <Form.Control
                  type="text"
                  value={editUserData.anydeskuser}
                  onChange={(e) =>
                    setEditUserData({
                      ...editUserData,
                      anydeskuser: e.target.value,
                    })
                  }
                  placeholder="anydesk user"
                  aria-label="Edit anydesk user"
                />
              </Form.Group>
            )}
            {editUserData.role === "customer" && (
              <Form.Group controlId="editanydeskpassword" className="mb-3">
                <Form.Label>{t('anydeskpassword')}</Form.Label>
                <Form.Control
                  type="text"
                  value={editUserData.anydeskpassword}
                  onChange={(e) =>
                    setEditUserData({
                      ...editUserData,
                      anydeskpassword: e.target.value,
                    })
                  }
                  placeholder="anydesk password"
                  aria-label="Edit anydesk password"
                />
              </Form.Group>
            )}
            {editUserData.role === "doctor" && (
              <Form.Group controlId="editDoctorType" className="mb-3">
                <Form.Label>{t('DoctorType')}</Form.Label>
                <Form.Select
                  value={editUserData.doctorType}
                  onChange={(e) =>
                    setEditUserData({
                      ...editUserData,
                      doctorType: e.target.value,
                    })
                  }
                  aria-label="Edit Doctor Type"
                >
                  <option>{t('Professor')}</option>
                  <option>{t('Doctor')}</option>
                </Form.Select>
              </Form.Group>
            )}

            {editUserData.role === "doctor" && (
              <>
                {/* Doctor specialization dropdown */}
                <Form.Group controlId="editSpecialization" className="mb-3">
                  <Form.Label>{t('specialization')}</Form.Label>
                  <Form.Select
                    value={editUserData.specialization}
                    onChange={e =>
                      setEditUserData({
                        ...editUserData,
                        specialization: e.target.value,
                      })
                    }
                    aria-label="Edit Specialization"
                  >
                    <option value="cornea">{t('cornea')}</option>
                    <option value="retina">{t('retina')}</option>
                    <option value="kids">{t('KIDS')}</option>
                    <option value="other">{t('OTHER')}</option>
                  </Form.Select>
                </Form.Group>

                {/* If “other” is selected, show a free‑text field */}
                {editUserData.specialization === 'other' && (
                  <Form.Group controlId="editOtherSpecialization" className="mb-3">
                    <Form.Label>{t('please_specify_specialization')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={editUserData.otherSpecialization}
                      onChange={e =>
                        setEditUserData({
                          ...editUserData,
                          otherSpecialization: e.target.value,
                        })
                      }
                      placeholder={t('enter_specialization')}
                      aria-label="Edit Other Specialization"
                    />
                  </Form.Group>
                )}
              </>
            )}

            {editUserData.role === "doctor" && (
              <>
                <Form.Group controlId="licenseUpload" className="mb-3">
                  <Form.Label>Upload License (PDF or Image)</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".pdf,image/*"
                    onChange={(e) => handleLicenseUpload(e.target.files[0])}
                  />
                </Form.Group>
                {/* –– **only admins** see the view button –– */}
                {editUserData.licenseFileUrl && currentUser.role === "admin" && (
                  <div className="d-flex justify-content-center mb-3">
                    <Button

                      onClick={() =>
                        window.open(editUserData.licenseFileUrl, "_blank")
                      }
                      variant="primary"
                      disabled={isProcessing}
                      className="btn-primarysmall"
                      aria-label="Save Edited User Button"
                    >
                      View Uploaded License
                    </Button>
                  </div>
                )}

              </>
            )}

            {/* Show password field ONLY if NOT admin, or if you specifically want it visible. */}
            {currentUser?.role !== "admin" && (
              <Form.Group controlId="editUserPassword">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={editUserData.password}
                  onChange={(e) => setEditUserData(prev => ({ ...prev, password: e.target.value }))}
                />
              </Form.Group>
            )}
            <DropdownButton
              id="edit-role-dropdown"
              title={`Role: ${editUserData.role}`}
              className="mb-3"
              aria-label="Edit Role Dropdown"
            >
              <Dropdown.Item onClick={() => setEditUserData({ ...editUserData, role: 'customer' })}>
                {t('Customer')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setEditUserData({ ...editUserData, role: 'doctor' })}>
                {t('Doctor')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setEditUserData({ ...editUserData, role: 'admin' })}>
                {t('Admin')}
              </Dropdown.Item>
            </DropdownButton>
            <Button
              variant="primary"
              onClick={saveEditedUser}
              disabled={isProcessing}
              className="w-100"
              aria-label="Save Edited User Button"
            >
              {t('save')}
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary"
            onClick={() => setShowEditUserModal(false)}
            aria-label="Cancel Edit User Modal">
            {t('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* History Modal */}
      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        centered
        dialogClassName="custom-history-modal" // <--- add this
        size="lg"
      >
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('History')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyTask && historyTask.versions.length > 0 ? (
            <Accordion defaultActiveKey="0">
              {historyTask.versions.map((version, index) => (
                <Accordion.Item eventKey={index.toString()} key={index}>
                  <Accordion.Header>
                    {t('Version')} {index + 1}
                  </Accordion.Header>
                  <Accordion.Body>
                    <p><strong>{t('Status')}</strong> {version.status}</p>
                    <p><strong>{t('doctor_notes')}</strong> {version.doctorNotes || "N/A"}</p>
                    <p><strong>{t('FinalDoctorNotes')}</strong> {version.finalDoctorNotes || "N/A"}</p>
                    <p><strong>{t('ReturnReason')}</strong> {version.returnReason || "N/A"}</p>
                    <p><strong>{t('Updated')}</strong> {new Date(version.updatedAt).toLocaleString()}</p>
                    <Button
                      variant="outline-primary"
                      className="custom-btn"
                      onClick={() => handleOpenFileBrowser(version)}
                    >
                      {t('Viewfile')}
                    </Button>

                    {version.files && version.files.length > 0 && (
                      <>
                        <h6>{t('Files')}</h6>
                        <ul className="list-disc list-inside">
                          {version.files.map(file => (
                            <li key={file.id} className="flex justify-between items-center mb-1">
                              <span className="truncated-file-name">{file.fileName}</span>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => handleDownloadFile(file.fileUrl, file.fileName)}
                                  aria-label={`Download ${file.fileName}`}
                                >
                                  {t('Downloadfile')}
                                </Button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          ) : (
            <p>{t('Noversionhistoryavailableforthistask')}</p>
          )}
        </Modal.Body>


        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            {t('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Signed Notes Modal */}
      <Modal show={showSignedNotesModal} onHide={() => setShowSignedNotesModal(false)} size="lg"
        centered>
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('SignedNotes')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {signedNotesFile ? (
            signedNotesFile.fileType === "application/pdf" ? (
              <iframe
                src={signedNotesFile.fileUrl}
                title={`Signed Notes of ${signedNotesFile.fileName}`}
                width="100%"
                height="500px"
                aria-label="Signed Notes PDF"
              ></iframe>
            ) : (
              <img
                src={signedNotesFile.fileUrl}
                alt={`Signed Notes of ${signedNotesFile.fileName}`}
                style={{ width: '100%', height: 'auto' }}
                aria-label="Signed Notes Image"
              />
            )
          ) : (
            <p>{t('Nosignednotesavailable')}</p>
          )}
          <SignatureCanvas
            penColor="black"
            minWidth={0.5}
            maxWidth={1.5}
            canvasProps={{
              width: 220,
              height: 70,
              className: "signature-canvas border"
            }}
            ref={signatureCanvasRef}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSignedNotesModal(false)} aria-label="Close Signed Notes Modal">
            {t('cancel')}
          </Button>
          {signedNotesFile && (
            <Button
              variant="primary"
              onClick={() => handleDownloadFile(signedNotesFile.fileUrl, signedNotesFile.fileName)}
              aria-label="Download Signed Notes"
            >
              {t('DownloadSignedNotes')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteTaskModal}
        onHide={cancelDeleteTask}
        centered
        backdrop="static" // Prevent closing by clicking outside if you prefer
        keyboard={true}  // Allow closing with Esc key
      >
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('ConfirmDeletion')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('Areyousureyouwanttodeletethistask')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDeleteTask}>
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDeleteTask}
            disabled={isProcessing}>

            {t('delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteUser} onHide={cancelDeleteUser}
        centered>
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('ConfirmDeletion')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('Areyousureyouwanttodeletethistask')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDeleteUser} aria-label="Cancel Deletion">
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDeleteUser} aria-label="Confirm Delete Task"
            disabled={isProcessing}>
            {t('delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Re-upload Modal */}
      <Modal show={showReuploadModal} onHide={() => setShowReuploadModal(false)}
        size="lg"
        centered>
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('reupload')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="reuploadFile" className="mb-3">
              <Form.Label>{t('SelectPDForJPEGFile')}</Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="application/pdf, image/jpeg"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    const files = Array.from(e.target.files).map(file => ({
                      id: uuidv4(),
                      fileType: file.type,
                      fileName: file.name,
                      file
                    }));

                    setReuploadFile(files);       // Save files for reupload
                    setStagedFiles(files);        // Send to metadata modal
                    setShowMetaModal(true);       // Open the modal
                    setIsReuploading(true);       // (Optional) flag to tell confirm handler this is a reupload
                  }
                }}
              />
            </Form.Group>
            {Array.isArray(reuploadFile) && reuploadFile.length > 0 && (
              <div className="mb-3">
                <strong>{t('selected_files')}:</strong>
                <ul className="list-disc list-inside">
                  {reuploadFile.map((f, idx) => (
                    // use idx in addition to id in case of duplicates
                    <li key={`${f.id}-${idx}`}>{f.fileName}</li>
                  ))}
                </ul>
              </div>
            )}

            <Button
              variant="primary"
              onClick={() => setShowReuploadModal(false)}
              aria-label="Confirm Re-upload Button"
            >
              {t('Confirm')}
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReuploadModal(false)} aria-label="Cancel Re-upload Modal">
            {t('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Task Detail Modal */}
      <Modal
        show={showTaskDetail}
        onHide={() => setShowTaskDetail(false)}
        size="lg"
        dialogClassName="custom-history-modal">
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('TaskDetails')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask ? (
            <>
              {/* Task Overview */}

              <p><strong>{t('TaskID')}</strong> {selectedTask.id || "N/A"}</p>

              {/* Patient Information */}
              <h5 className="mt-3">{t('PatientInformation')}</h5>
              <p><strong>{t('TaskName')}</strong> {selectedTask.name || "N/A"}</p>
              <p><strong>{t('age')}</strong> {selectedTask.age || "N/A"}</p>
              <p><strong>{t('patient_id')}</strong> {selectedTask.patientID || "None"}</p>
              <p><strong>{t('diseases')}</strong> {selectedTask.diseases || "None"}</p>

              {/* Additional Notes */}
              <h5 className="mt-3">{t('additional_notes')}</h5>
              <p><strong>{t('Notes')}</strong> {selectedTask.taskNotes || "N/A"}</p>

              {/* Uploaded By */}
              <h5 className="mt-3">{t('UploadedBy')}</h5>
              <p>
                <strong>{t('UploadedBy')}</strong> {users.find(u => u.id === selectedTask.uploadedBy)?.username || "N/A"}
              </p>

              {/* Doctor Review */}
              <h5 className="mt-3">{t('DoctorReview')}</h5>
              {selectedTask.versions && selectedTask.versions.length > 0 ? (
                <>
                  {/* Get the latest version */}
                  {(() => {
                    const latestVersion = selectedTask.versions[selectedTask.versions.length - 1];
                    return (
                      <>
                        <p><strong>{t('Status')}</strong> {latestVersion.status || "N/A"}</p>
                        <p>
                          <strong>{t('Doctor')}</strong>{" "}
                          {latestVersion.acceptedBy
                            ? users.find(u => u.id === latestVersion.acceptedBy)?.username || "N/A"
                            : "N/A"}
                        </p>
                        <p><strong>{t('ReturnReason')}</strong> {latestVersion.returnReason || "N/A"}</p>
                        <p>
                          <strong>{t('Updated')}</strong>{" "}
                          {latestVersion.updatedAt
                            ? new Date(latestVersion.updatedAt).toLocaleString()
                            : "N/A"}
                        </p>
                        <Button
                          variant="outline-primary"
                          className="custom-btn"
                          onClick={() => handleOpenFileBrowser(latestVersion)}
                        >
                          {t('Viewfile')}
                        </Button>
                        {/* Files Section */}
                        {latestVersion.files && latestVersion.files.length > 0 && (() => {
                          const taskFiles = latestVersion.files.filter(
                            f => !/signed/i.test(f.fileName)
                          );
                          const signedFiles = latestVersion.files.filter(
                            f => /signed/i.test(f.fileName)
                          );

                          return (
                            <>
                              {/* Title for normal task files */}
                              {taskFiles.length > 0 && (
                                <>
                                  <h5 className="mt-3">{t('TaskFiles')}</h5>
                                  <ul className="list-unstyled mb-0">
                                    {taskFiles.map((file, idx) => (
                                      <li key={`${file.id}-${idx}`} className="mb-3">
                                        <div className="d-flex w-100">
                                          <strong className="flex-fill text-truncate">
                                            {file.fileName}
                                          </strong>
                                        </div>
                                        <div style={{ fontStyle: 'italic', color: '#555' }}>
                                          {file.eye || '—'}
                                          {file.customTitle ? ` — ${file.customTitle}` : ''}
                                        </div>
                                        <div className="d-flex gap-2">
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleDownloadFile(file.fileUrl, file.fileName)}
                                          >
                                            {t('Downloadfile')}
                                          </Button>
                                          {currentUser?.role === "customer" &&
                                            (latestVersion.status === "Returned" || latestVersion.status === "Not Accepted") && (
                                              <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeleteFile(selectedTask, file.id)}
                                                disabled={isProcessing}
                                              >
                                                {t('delete')}
                                              </Button>
                                            )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}

                              {/* Title for signed file(s) */}
                              {signedFiles.length > 0 && (
                                <>
                                  <h5 className="mt-3">{t('SignedFile')}</h5>
                                  <ul className="list-unstyled mb-0">
                                    {signedFiles.map(file => (
                                      <li key={file.id} className="mb-3">
                                        <div className="d-flex w-100 align-items-center">
                                          <strong
                                            className="flex-fill text-truncate"
                                            style={{ color: 'green' }}
                                          >
                                            {file.fileName}
                                          </strong>
                                          <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleDownloadFile(file.fileUrl, file.fileName)}
                                          >
                                            {t('Downloadfile')}
                                          </Button>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </>
                          );
                        })()}


                      </>
                    );
                  })()}
                </>
              ) : (
                <p className="text-danger">Error: No version data available.</p>
              )}
            </>
          ) : (
            <p className="text-danger">Error: No task selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTaskDetail(false)} aria-label="Close Task Detail Modal">
            {t('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* file view */}
      <Modal
        show={showFilePreview}
        onHide={() => setShowFilePreview(false)}
        dialogClassName="modal-dialog-20cm"
        contentClassName="modal-content-20cm"
        centered
      >
        <Modal.Header
          className="custom-card-header d-flex align-items-center justify-content-between w-100 px-3 px-sm-4"
          closeButton
          closeVariant="white">
          <Modal.Title>
            {fileList[currentFileIndex]?.eye && (
              <span>{fileList[currentFileIndex].eye} — </span>
            )}
            {fileList[currentFileIndex]?.customTitle
              ? fileList[currentFileIndex].customTitle
              : fileList[currentFileIndex]?.fileName || t("FileBrowser")}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="modal-body-20cm">
          {fileList.length > 0 ? (
            fileList[currentFileIndex].fileType === "application/pdf" ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <iframe
                  src={fileList[currentFileIndex].fileUrl}
                  title="PDF Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    border: "none",
                  }}
                />
              </div>
            ) : (
              <div
                className="magnifier-container"
                onMouseEnter={() => setIsMagnified(true)}
                onMouseLeave={() => setIsMagnified(false)}
                onMouseMove={e => {
                  const { left, top, width, height } =
                    e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - left;
                  const y = e.clientY - top;
                  setMousePos({
                    x,
                    y,
                    imgW: width,
                    imgH: height,
                    pageX: e.clientX,
                    pageY: e.clientY,
                  });
                }}
                onClick={() => setIsMagnified(prev => !prev)}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <img
                  src={fileList[currentFileIndex].fileUrl}
                  alt="Image Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
                {isMagnified &&
                  (() => {
                    const zoom = 2;
                    const lensDiam = 300;
                    const r = lensDiam / 2;
                    const { x: cx, y: cy } = mousePos;

                    return createPortal(
                      <div
                        className="magnifier-lens"
                        style={{
                          position: "fixed",
                          zIndex: 2000,
                          left: `${mousePos.pageX - r}px`,
                          top: `${mousePos.pageY - r}px`,
                          width: `${lensDiam}px`,
                          height: `${lensDiam}px`,
                          backgroundImage: `url(${fileList[currentFileIndex].fileUrl})`,
                          backgroundSize: `${mousePos.imgW * zoom}px ${mousePos.imgH *
                            zoom}px`,
                          backgroundPosition: `-${cx * zoom - r}px -${cy * zoom - r}px`,
                        }}
                      />,
                      document.body
                    );
                  })()}
              </div>
            )
          ) : (
            <p>No files to display.</p>
          )}
        </Modal.Body>


        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="outline-primary"
            className="custom-btn"
            onClick={() =>
              setCurrentFileIndex(
                (currentFileIndex - 1 + fileList.length) % fileList.length
              )
            }
          >
            {t("Previous")}
          </Button>

          <span>
            {currentFileIndex + 1} of {fileList.length}
          </span>

          <Button
            variant="outline-primary"
            className="custom-btn"
            onClick={() =>
              setCurrentFileIndex((currentFileIndex + 1) % fileList.length)
            }
          >
            {t("Next")}
          </Button>
        </Modal.Footer>
      </Modal>



      <Modal show={showExpandedEditor} onHide={() => setShowExpandedEditor(false)} centered size="lg"
      >
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('EditNote')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={10}
            value={unsavedNotes[currentEditingTaskId] || ""}
            onChange={(e) =>
              setUnsavedNotes({ ...unsavedNotes, [currentEditingTaskId]: e.target.value })
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button className="custom-btn" onClick={() => setShowExpandedEditor(false)}>
            {t('Close')}
          </Button>
          <Button className="custom-btn" onClick={() => {
            handleSaveDoctorNotes(currentEditingTaskId);
            setShowExpandedEditor(false);
          }}>
            {t('save')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={storeInfo !== null} onHide={() => setStoreInfo(null)} size="lg"
        centered>
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('StoreInfo')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {storeInfo ? (
            <>
              <p><strong>{t('StoreName')}:</strong> {storeInfo.storeName || "N/A"}</p>
              <p><strong>{t('Name')}:</strong> {storeInfo.username || "N/A"}</p>
              <p><strong>{t('LastName')}:</strong> {storeInfo.lastName || "N/A"}</p>
              <p><strong>{t('PhoneNumber')}:</strong> {storeInfo.PhoneNumber || "N/A"}</p>
              <p><strong>{t('anydeskuser')}:</strong> {storeInfo.anydeskuser || "N/A"}</p>
              <p><strong>{t('anydeskpassword')}:</strong> {storeInfo.anydeskpassword || "N/A"}</p>
            </>
          ) : (
            <p>{t('NoDataFound')}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary"
            className="custom-btn" onClick={() => setStoreInfo(null)}>
            {t('Close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showReturnedModal}
        onHide={() => setShowReturnedModal(false)}
        size="lg"
        centered
      >
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title> {t('ReturnedTasks')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {returnedModalTasks.length === 0 ? (
            <p>{t('NoReturnedTasks')}</p>
          ) : (
            <Accordion>
              {returnedModalTasks.map(task => {
                const v = getCurrentVersion(task);
                return (
                  <Accordion.Item eventKey={task.id} key={task.id}>
                    <Accordion.Header>
                      {task.storeName} —  {task.name} — {v.updatedAt.split('T')[0]}
                    </Accordion.Header>
                    <Accordion.Body>
                      <p><strong>{t('TaskName')}:</strong> {task.name}</p>
                      <p><strong>{t('ReturnReason')}:</strong> {v.returnReason}</p>
                      <div className="d-flex gap-2 mt-3">
                        <Button
                          variant="primary"
                          onClick={() => openTaskDetail(task)}
                        >
                          {t('view_details')}
                        </Button>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReturnedModal(false)}>
            {t('close')}
          </Button>
        </Modal.Footer>
      </Modal>


      <Modal show={showDoctorEditModal} onHide={() => setShowDoctorEditModal(false)}>
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('UpdatePersonalDetails')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>{t('PhoneNumber')}</Form.Label>
              <Form.Control
                type="text"
                value={doctorDetails.PhoneNumber}
                onChange={(e) => setDoctorDetails(prev => ({ ...prev, PhoneNumber: e.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('IDNumber')}</Form.Label>
              <Form.Control
                type="text"
                value={doctorDetails.idNumber}
                onChange={(e) => setDoctorDetails(prev => ({ ...prev, idNumber: e.target.value }))}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('LicenseNumber')}</Form.Label>
              <Form.Control
                type="text"
                value={doctorDetails.licenseNumber}
                onChange={(e) => setDoctorDetails(prev => ({ ...prev, licenseNumber: e.target.value }))}
              />
            </Form.Group>
            <Form.Group controlId="editSpecialization" className="mb-3">
              <Form.Label>{t('specialization')}</Form.Label>
              <Form.Select
                value={doctorDetails.specialization}
                onChange={e => setDoctorDetails(prev => ({ ...prev, specialization: e.target.value }))}
                aria-label="Edit Specialization"
              >
                <option value="cornea">{t('cornea')}</option>
                <option value="retina">{t('retina')}</option>
                <option value="kids">{t('KIDS')}</option>
                <option value="other">{t('OTHER')}</option>
              </Form.Select>
            </Form.Group>
            {doctorDetails.specialization === 'other' && (
              <Form.Group controlId="editOtherSpecialization" className="mb-3">
                <Form.Label>{t('Pleasespecifyspecialization')}</Form.Label>
                <Form.Control
                  type="text"
                  value={doctorDetails.otherSpecialization}
                  onChange={e => setDoctorDetails(prev => ({ ...prev, otherSpecialization: e.target.value }))}
                  placeholder="Enter specialization"
                  aria-label="Edit Other Specialization"
                />
              </Form.Group>
            )}
            <Form.Group>
              <Form.Label>{t('UploadLicense')}</Form.Label>
              <Form.Control type="file" onChange={(e) => setLicenseFile(e.target.files?.[0] || null)} />
              {doctorDetails.licenseFileUrl ? (
                <div className="mb-2 text-success">
                  ✅ Already uploaded: <a href={doctorDetails.licenseFileUrl} target="_blank" rel="noopener noreferrer">
                    View current license file
                  </a>
                </div>
              ) : (
                <div className="mb-2 text-muted">{t('Nolicenseuploaded')}</div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDoctorEditModal(false)}>{t('cancel')}</Button>
          <Button variant="primary" onClick={handleSaveDetails}>{t('save')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCustomerEditModal}
        onHide={() => setShowCustomerEditModal(false)}
      >
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('UpdateYourDetails')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('StoreName')}</Form.Label>
              <Form.Control
                type="text"
                value={customerDetails.storeName}
                onChange={e => setCustomerDetails(prev => ({ ...prev, storeName: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('LastName')}</Form.Label>
              <Form.Control
                type="text"
                value={customerDetails.lastName}
                onChange={e => setCustomerDetails(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('PhoneNumber')}</Form.Label>
              <Form.Control
                type="text"
                value={customerDetails.PhoneNumber}
                onChange={e => setCustomerDetails(prev => ({ ...prev, PhoneNumber: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('Address')}</Form.Label>
              <Form.Control
                type="text"
                value={customerDetails.address}
                onChange={e => setCustomerDetails(prev => ({ ...prev, address: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('AnydeskUser')}</Form.Label>
              <Form.Control
                type="text"
                value={customerDetails.anydeskuser}
                onChange={e => setCustomerDetails(prev => ({ ...prev, anydeskuser: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('AnydeskPassword')}</Form.Label>
              <Form.Control
                type="password"
                value={customerDetails.anydeskpassword}
                onChange={e => setCustomerDetails(prev => ({ ...prev, anydeskpassword: e.target.value }))}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCustomerEditModal(false)}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSaveCustomerDetails}>
            {t('save')}
          </Button>
        </Modal.Footer>
      </Modal>



      <Modal
        show={showReqFieldsModal}
        onHide={() => setShowReqFieldsModal(false)}
      >
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t("RequiredFieldsSettings")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>


          <Card className="shadow-sm mt-4">
            <Card.Header className="custom-card-header">
              {t('FeatureToggles')}
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group
                  controlId="toggleDoctorEdit"
                  className="d-flex justify-content-between align-items-center mb-3"
                >
                  <Switch
                    checked={allowDoctorEdit}
                    onChange={async checked => {
                      await setDoc(
                        doc(db, "settings", "userOptions"),
                        { allowDoctorEdit: checked },
                        { merge: true }
                      );
                    }}
                  />
                  <Form.Label className="mb-0 text-end flex-grow-1 ms-3">
                    {t('EnableDoctorEdit')}
                  </Form.Label>
                </Form.Group>

                <Form.Group
                  controlId="toggleCustomerEdit"
                  className="d-flex justify-content-between align-items-center"
                >
                  <Switch
                    checked={allowCustomerEdit}
                    onChange={async checked => {
                      await setDoc(
                        doc(db, "settings", "userOptions"),
                        { allowCustomerEdit: checked },
                        { merge: true }
                      );
                    }}
                  />
                  <Form.Label className="mb-0 text-end flex-grow-1 ms-3">
                    {t('EnableCustomerEdit')}
                  </Form.Label>
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
          <Form.Group controlId="autoSignDelay" className="d-flex align-items-center mb-3">
            <Form.Label className="me-auto mb-0">
              {t("Auto Sign Delay (minutes)")}
            </Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={autoSignDelay}
              onChange={async e => {
                const mins = parseInt(e.target.value, 10) || 0;
                setAutoSignDelay(mins);
                await setDoc(
                  doc(db, "settings", "userOptions"),
                  { autoSignDelayMinutes: mins },
                  { merge: true }
                );
              }}
              className="ms-3"
              style={{ width: "5rem", marginLeft: "1.5rem" }}
            />
          </Form.Group>

          <Form.Group
            controlId="toggleReminderSMS"
            className="d-flex justify-content-between align-items-center mb-3"
          >
            <Switch
              className="me-3"         // ← add this
              checked={enableReminderSMS}
              onChange={async checked => {
                await setDoc(
                  doc(db, "settings", "userOptions"),
                  { enableReminderSMS: checked },
                  { merge: true }
                );
              }}
            />
            <Form.Label className="mb-0 text-end flex-grow-1">
              {t("Enable reminder SMS to doctors")}
            </Form.Label>
          </Form.Group>

          <Form.Group
            controlId="reminderDelayHours"
            className="d-flex justify-content-between align-items-center mb-3"
          >
            <Form.Control
              type="number"
              className="me-3"
              min={1}
              value={reminderDelayHours}
              onChange={async e => {
                const hrs = parseInt(e.target.value, 10) || 0;
                setReminderDelayHours(hrs);
                await setDoc(
                  doc(db, "settings", "userOptions"),
                  { reminderDelayHours: hrs },
                  { merge: true }
                );
              }}
              style={{ width: "4rem" }}
            />
            <Form.Label className="mb-0 text-end flex-grow-1 ms-5">
              {t("Reminder(hours)")}
            </Form.Label>
          </Form.Group>


        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReqFieldsModal(false)}>
            {t("close")}
          </Button>
        </Modal.Footer>
      </Modal>


      <Modal show={showNotifications} onHide={() => setShowNotifications(false)}
        centered>
        <Modal.Header className="custom-card-header" closeButton>
          <Modal.Title>{t('Notifications')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentUser?.role === 'doctor' ? (
            <>
              {/** Doctor: show all “Not Accepted” tasks */}
              {(() => {
                const newTasks = tasks.filter(task => {
                  const currentVersion = getCurrentVersion(task);
                  if (currentVersion.status !== 'Not Accepted') return false;

                  const uploader = users.find(u => u.id === task.uploadedBy);
                  if (!uploader || uploader.role !== "customer") return false;

                  const mainDoctorId = uploader.mainDoctor;
                  const secondaryDoctorId = uploader.secondaryDoctor;
                  const mainDoctor = users.find(u => u.id === mainDoctorId);

                  const isMain = currentUser.id === mainDoctorId;
                  const isSecondary = currentUser.id === secondaryDoctorId;

                  if (isMain && mainDoctor?.isAvailable) return true;
                  if (isSecondary && (!mainDoctor || !mainDoctor.isAvailable)) return true;

                  return false;
                });

                return newTasks.length > 0 ? (
                  <>
                    <h5>{t('NewTasks')} ({newTasks.length})</h5>
                    <ListGroup>
                      {newTasks.map(task => (
                        <ListGroup.Item
                          key={task.id}
                          action
                          onClick={() => {
                            openTaskDetail(task);
                            // optionally mark as viewed for doctor here
                          }}
                        >
                          {task.name} — {getCurrentVersion(task).updatedAt.split('T')[0]}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </>
                ) : (
                  <p>{t('NonewTasks')}</p>
                );
              })()}
            </>
          ) : (
            <>
              {/** Customer: existing signed/returned view */}
              {newSignedCount > 0 && (
                <>
                  <h5>{t('NewSignedReports')} ({newSignedCount})</h5>
                  <ListGroup className="mb-3">
                    {tasks
                      .filter(task => {
                        const v = getCurrentVersion(task);
                        return v.status === "Finished" && !v.customerViewed;
                      })
                      .map(task => {
                        const v = getCurrentVersion(task);
                        return (
                          <ListGroup.Item
                            key={task.id}
                            action
                            onClick={() => {
                              openTaskDetail(task);
                              markAsViewed(task.id, "Finished");
                            }}
                          >
                            {task.name} — {t('signedon')} {v.updatedAt.split('T')[0]}
                          </ListGroup.Item>
                        );
                      })}
                  </ListGroup>
                </>
              )}

              {newReturnedCount > 0 && (
                <>
                  <h5>{t('NewReturnedTasks')} ({newReturnedCount})</h5>
                  <ListGroup className="mb-3">
                    {tasks
                      .filter(task => {
                        const v = getCurrentVersion(task);
                        return v.status === "Returned" && !v.customerReturnViewed;
                      })
                      .map(task => {
                        const v = getCurrentVersion(task);
                        return (
                          <ListGroup.Item
                            key={task.id}
                            action
                            onClick={() => {
                              openTaskDetail(task);
                              markAsViewed(task.id, "Returned");
                            }}
                          >
                            {task.name} — {t('returnedon')} {v.updatedAt.split('T')[0]}
                          </ListGroup.Item>
                        );
                      })}
                  </ListGroup>
                </>
              )}
              {(newSignedCount + newReturnedCount) === 0 && <p>{t('Nonewnotifications')}</p>}
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
        centered
      >
        <Modal.Header
          className="custom-card-header"
          closeButton
          closeVariant="white"
        >
          <Modal.Title>{t("Preview Scan")}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: "center", padding: 0 }}>
          <img
            src={previewUrl}
            alt="Preview"
            style={{ maxWidth: "100%", maxHeight: "80vh" }}
          />
        </Modal.Body>
      </Modal>



    </div >
  );
}

export default App;
